import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Heart, MessageCircle, Share2, UserPlus, UserMinus, Users, Video, Play, Edit2, Trash2, Eye, ThumbsUp, Volume2, VolumeX, X, Star, Plus, Info, Bell, BellOff, ExternalLink } from 'lucide-react';
import WhisperIcon from './WhisperIcon';
import WhisperModal from './WhisperModal';
import VideoUploadModal from './VideoUploadModal';
import PromoVideoPlayer from './PromoVideoPlayer';
import VideoEditModal from './VideoEditModal';
import EditProfileModal from './EditProfileModal';
import { AddGearReviewForm } from './MyGearSection';
import DiscordShareModal from './DiscordShareModal';
import ArtistNotificationBell from './ArtistNotificationBell';
import MutualFriendsModal from './MutualFriendsModal';
import UserBadge, { UserBadgeGroup } from './UserBadge';
import IdHunterMedal from './IdHunterMedal';
import { HunterChip, HunterRecordCard } from './HunterStats';

// Compact Share Profile button — sits next to the username on a profile page.
// Visible to everyone visiting the profile (viral upside). Copies link + opens
// native share sheet on mobile if available.
const ShareProfileButton = ({ userId, userName }) => {
  const [copied, setCopied] = React.useState(false);
  const url = `${window.location.origin}/profile/${userId}`;
  const handleShare = async () => {
    const shareData = {
      title: `${userName || 'BeatSeek user'} on BEATSEEK`,
      text: `Check out ${userName || 'this profile'} on BEATSEEK`,
      url,
    };
    try {
      if (typeof navigator !== 'undefined' && navigator.share) {
        await navigator.share(shareData);
        return;
      }
    } catch (_e) { /* fall through to clipboard */ }
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (_e) { /* ignore */ }
  };
  return (
    <button
      data-testid="profile-share-btn"
      onClick={handleShare}
      title="Share profile"
      className="relative inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border border-fuchsia-400/70 text-white bg-fuchsia-700 hover:bg-fuchsia-600 shadow-lg transition-colors"
    >
      <Share2 size={14} />
      Share Profile
      {copied && (
        <span className="absolute -top-7 left-1/2 -translate-x-1/2 bg-green-500 text-white text-[10px] px-2 py-0.5 rounded whitespace-nowrap">
          ✓ Link Copied
        </span>
      )}
    </button>
  );
};

const PublicProfilePage = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [currentUser, setCurrentUser] = useState(null);
  const [showMutualModal, setShowMutualModal] = useState(false);
  const [topTracksView, setTopTracksView] = useState('month'); // 'month' or 'alltime'
  const [monthlyTop10, setMonthlyTop10] = useState([]);
  const [alltimeTop10, setAlltimeTop10] = useState([]);
  const [loadingTop10, setLoadingTop10] = useState(false);
  const [monthlyPage, setMonthlyPage] = useState(0); // Pagination for monthly top 10
  const [alltimePage, setAlltimePage] = useState(0); // Pagination for all-time top 10
  const [activityData, setActivityData] = useState([]);
  const [communityData, setCommunityData] = useState(null);
  const [loadingActivity, setLoadingActivity] = useState(false);
  const [loadingCommunity, setLoadingCommunity] = useState(false);
  const [viewAllModal, setViewAllModal] = useState({ isOpen: false, section: null, users: [] });
  const [badgesData, setBadgesData] = useState(null);
  const [loadingBadges, setLoadingBadges] = useState(false);
  const [isFollowing, setIsFollowing] = useState(false);
  const [isFriend, setIsFriend] = useState(false);
  const [friendRequestStatus, setFriendRequestStatus] = useState(null); // 'pending', 'accepted', null
  const [promoVideo, setPromoVideo] = useState(null);
  const [loadingVideo, setLoadingVideo] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showEditProfileModal, setShowEditProfileModal] = useState(false);
  const [avatarFailed, setAvatarFailed] = useState(false);
  const [videoToEdit, setVideoToEdit] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [volumeExpanded, setVolumeExpanded] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolume] = useState(0.5);
  const [isLiked, setIsLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  const [showCompletionBanner, setShowCompletionBanner] = useState(false);

  // Local WhisperModal state (ProfileView renders its own modal since it lives outside the / route's ElectroShazam component)
  const [showLocalWhisper, setShowLocalWhisper] = useState(false);
  const [globalEmotes, setGlobalEmotes] = useState([]);
  const [customEmotes, setCustomEmotes] = useState([]);
  const videoRef = useRef(null);
  const controlsTimeoutRef = useRef(null);
  const [marketplaceData, setMarketplaceData] = useState({ gear: [], merch: [] });
  const [loadingMarketplace, setLoadingMarketplace] = useState(false);
  const [marketplaceSearch, setMarketplaceSearch] = useState('');
  const [marketplaceCategory, setMarketplaceCategory] = useState('all'); // 'all', 'gear', 'merch'
  
  // Promos state
  const [promosData, setPromosData] = useState([]);
  const [loadingPromos, setLoadingPromos] = useState(false);
  const [showEditPromoModal, setShowEditPromoModal] = useState(false);
  const [promoToEdit, setPromoToEdit] = useState(null);
  
  // Favorites state
  const [savedVenues, setSavedVenues] = useState([]);
  const [favoriteTracks, setFavoriteTracks] = useState([]);
  const [loadingFavorites, setLoadingFavorites] = useState(false);
  const [showFavoritesModal, setShowFavoritesModal] = useState(false);
  const [favoritesActiveTab, setFavoritesActiveTab] = useState('music'); // 'music' or 'venues'
  
  // My Gear state
  const [userGear, setUserGear] = useState([]);
  const [loadingGear, setLoadingGear] = useState(false);
  const [showAddGearForm, setShowAddGearForm] = useState(false);
  
  // Discord Share state
  const [showDiscordShareModal, setShowDiscordShareModal] = useState(false);
  const [discordShareData, setDiscordShareData] = useState(null);
  
  // Following Artists state
  const [followedArtists, setFollowedArtists] = useState([]);
  const [loadingFollowedArtists, setLoadingFollowedArtists] = useState(false);
  
  // Check if user came from globe
  const cameFromGlobe = new URLSearchParams(window.location.search).get('fromGlobe') === 'true';

  // Load current user from localStorage
  useEffect(() => {
    const userData = localStorage.getItem('beatseek_user');
    console.log('🔍 ProfileView - localStorage user data:', userData);
    if (userData) {
      try {
        const parsed = JSON.parse(userData);
        console.log('✅ ProfileView - Parsed current user:', parsed);
        setCurrentUser(parsed);
      } catch (err) {
        console.error('Error parsing user data:', err);
      }
    } else {
      console.log('⚠️ ProfileView - No user data in localStorage');
    }
  }, []);

  // Fetch profile data
  useEffect(() => {
    const fetchProfileData = async () => {
      try {
        const API = process.env.REACT_APP_BACKEND_URL || '';
        const viewerId = currentUser?.user_id || currentUser?.id;
        const url = viewerId 
          ? `${API}/api/profile/${userId}?viewer_id=${viewerId}`
          : `${API}/api/profile/${userId}`;
        
        const response = await fetch(url);
        
        if (!response.ok) {
          throw new Error('User not found');
        }
        
        const data = await response.json();
        setProfileData(data);

        // Record profile view
        if (viewerId && viewerId !== userId) {
          fetch(`${API}/api/profile/${userId}/view`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ viewer_id: viewerId })
          }).catch(err => console.error('Error recording view:', err));
        }
      } catch (err) {
        console.error('Error fetching profile:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (userId) {
      fetchProfileData();
    }
  }, [userId, currentUser]);

  // Fetch saved venues count when viewing own profile
  useEffect(() => {
    if (currentUser && userId && (currentUser.user_id === userId || currentUser.id === userId)) {
      fetchFavorites();
    }
  }, [userId, currentUser]);

  // Mobile rotation suggestion - show once per session
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const isMobile = window.innerWidth <= 768;
    const isPortrait = window.innerHeight > window.innerWidth;
    const alreadyShown = sessionStorage.getItem('profile_rotation_alert_shown');
    if (isMobile && isPortrait && !alreadyShown) {
      const t = setTimeout(() => {
        alert('📱 Turn your phone sideways for a better viewing experience!');
        sessionStorage.setItem('profile_rotation_alert_shown', 'true');
      }, 500);
      return () => clearTimeout(t);
    }
  }, []);


  // Fetch activity data when Activity tab is active
  useEffect(() => {
    const fetchActivity = async () => {
      if (activeTab === 'activity' && userId) {
        setLoadingActivity(true);
        try {
          const API = process.env.REACT_APP_BACKEND_URL || '';
          const response = await fetch(`${API}/api/profile/${userId}/activity`);
          if (response.ok) {
            const data = await response.json();
            setActivityData(data.activities || []);
          }
        } catch (err) {
          console.error('Error fetching activity:', err);
        } finally {
          setLoadingActivity(false);
        }
      }
    };

    fetchActivity();
  }, [activeTab, userId]);

  // Fetch community data when Community tab is active
  useEffect(() => {
    const fetchCommunity = async () => {
      if (activeTab === 'community' && userId) {
        setLoadingCommunity(true);
        try {
          const API = process.env.REACT_APP_BACKEND_URL || '';
          const viewerId = currentUser?.user_id || currentUser?.id;
          const url = viewerId 
            ? `${API}/api/profile/${userId}/community?viewer_id=${viewerId}`
            : `${API}/api/profile/${userId}/community`;
          
          const response = await fetch(url);
          if (response.ok) {
            const data = await response.json();
            setCommunityData(data);
          }
        } catch (err) {
          console.error('Error fetching community:', err);
        } finally {
          setLoadingCommunity(false);
        }
      }
    };

    fetchCommunity();
  }, [activeTab, userId, currentUser]);

  // Fetch badges data when Badges tab is active
  useEffect(() => {
    const fetchBadges = async () => {
      if (activeTab === 'badges' && userId) {
        setLoadingBadges(true);
        try {
          const API = process.env.REACT_APP_BACKEND_URL || '';
          const response = await fetch(`${API}/api/profile/${userId}/badges`);
          if (response.ok) {
            const data = await response.json();
            setBadgesData(data);
          }
        } catch (err) {
          console.error('Error fetching badges:', err);
        } finally {
          setLoadingBadges(false);
        }
      }
    };

    fetchBadges();
  }, [activeTab, userId]);

  // Fetch marketplace data when Marketplace tab is active
  useEffect(() => {
    const fetchMarketplace = async () => {
      if (activeTab === 'marketplace' && userId) {
        setLoadingMarketplace(true);
        try {
          const API = process.env.REACT_APP_BACKEND_URL || '';
          
          // Fetch both gear and merch listings
          const [gearResponse, merchResponse] = await Promise.all([
            fetch(`${API}/api/marketplace/gear/user/${userId}`),
            fetch(`${API}/api/marketplace/merch/user/${userId}`)
          ]);
          
          const gear = gearResponse.ok ? await gearResponse.json() : [];
          const merch = merchResponse.ok ? await merchResponse.json() : [];
          
          setMarketplaceData({ gear, merch });
        } catch (error) {
          console.error('Error fetching marketplace data:', error);
          setMarketplaceData({ gear: [], merch: [] });
        } finally {
          setLoadingMarketplace(false);
        }
      }
    };

    fetchMarketplace();
  }, [activeTab, userId]);

  // Fetch promos data when Promos tab is active
  useEffect(() => {
    const fetchPromos = async () => {
      if (activeTab === 'promos' && userId) {
        setLoadingPromos(true);
        try {
          const API = process.env.REACT_APP_BACKEND_URL || '';
          const response = await fetch(`${API}/api/promos/user/${userId}`);
          
          if (response.ok) {
            const data = await response.json();
            setPromosData(data.promos || []);
          } else {
            setPromosData([]);
          }
        } catch (error) {
          console.error('Error fetching promos:', error);
          setPromosData([]);
        } finally {
          setLoadingPromos(false);
        }
      }
    };

    fetchPromos();
  }, [activeTab, userId]);

  // Fetch user gear when Gear tab is active
  useEffect(() => {
    const fetchUserGear = async () => {
      if (activeTab === 'gear' && userId) {
        setLoadingGear(true);
        try {
          const API = process.env.REACT_APP_BACKEND_URL || '';
          const response = await fetch(`${API}/api/gear/user/${userId}/public`);
          
          if (response.ok) {
            const data = await response.json();
            setUserGear(data.gear || []);
          } else {
            setUserGear([]);
          }
        } catch (error) {
          console.error('Error fetching user gear:', error);
          setUserGear([]);
        } finally {
          setLoadingGear(false);
        }
      }
    };

    fetchUserGear();
  }, [activeTab, userId]);

  // Fetch followed artists when the tab is active
  useEffect(() => {
    const fetchFollowedArtists = async () => {
      if (activeTab !== 'following-artists') return;
      
      const token = localStorage.getItem('beatseek_token') || localStorage.getItem('token');
      if (!token) return;
      
      // Only show for own profile
      const isOwnProfile = currentUser && (currentUser.id === userId || currentUser.user_id === userId);
      if (!isOwnProfile) return;

      setLoadingFollowedArtists(true);
      try {
        const API = process.env.REACT_APP_BACKEND_URL || '';
        const response = await fetch(`${API}/api/artists/following`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (response.ok) {
          const data = await response.json();
          setFollowedArtists(data.artists || []);
        }
      } catch (error) {
        console.error('Error fetching followed artists:', error);
      } finally {
        setLoadingFollowedArtists(false);
      }
    };

    fetchFollowedArtists();
  }, [activeTab, userId, currentUser]);

  // Fetch favorites (saved venues and favorite tracks)
  const fetchFavorites = async () => {
    const token = localStorage.getItem('beatseek_token') || localStorage.getItem('token');
    if (!token) return;

    setLoadingFavorites(true);
    try {
      const API = process.env.REACT_APP_BACKEND_URL || '';
      
      // Fetch saved venues
      const venuesResponse = await fetch(`${API}/api/directory/venues/saved`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (venuesResponse.ok) {
        const venuesData = await venuesResponse.json();
        setSavedVenues(venuesData.saved_venues || []);
      }

      // Fetch favorite tracks
      const tracksResponse = await fetch(`${API}/api/users/favorites`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (tracksResponse.ok) {
        const tracksData = await tracksResponse.json();
        setFavoriteTracks(tracksData.favorites || []);
      }
    } catch (error) {
      console.error('Error fetching favorites:', error);
    } finally {
      setLoadingFavorites(false);
    }
  };

  // Remove a saved venue
  const removeSavedVenue = async (googlePlaceId) => {
    const token = localStorage.getItem('beatseek_token') || localStorage.getItem('token');
    if (!token) return;

    try {
      const API = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${API}/api/directory/venues/save/${googlePlaceId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        setSavedVenues(prev => prev.filter(v => v.google_place_id !== googlePlaceId));
      }
    } catch (error) {
      console.error('Error removing saved venue:', error);
    }
  };

  // Remove a favorite track
  const removeFavoriteTrack = async (trackId) => {
    const token = localStorage.getItem('beatseek_token') || localStorage.getItem('token');
    if (!token) return;

    try {
      const API = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${API}/api/users/favorites/${trackId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        setFavoriteTracks(prev => prev.filter(t => t.id !== trackId));
      }
    } catch (error) {
      console.error('Error removing favorite track:', error);
    }
  };

  // Fetch promo video
  useEffect(() => {
    const fetchPromoVideo = async () => {
      if (!userId) return;
      
      setLoadingVideo(true);
      try {
        const API = process.env.REACT_APP_BACKEND_URL || '';
        const token = localStorage.getItem('beatseek_token');
        
        const headers = {};
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
        
        const response = await fetch(`${API}/api/promo-videos/user/${userId}`, {
          headers
        });
        
        if (response.ok) {
          const data = await response.json();
          setPromoVideo(data);
          setLikeCount(data.likes || 0);
        } else if (response.status === 404) {
          // User doesn't have a video
          setPromoVideo(null);
        }
      } catch (err) {
        console.error('Error fetching promo video:', err);
      } finally {
        setLoadingVideo(false);
      }
    };

    fetchPromoVideo();
  }, [userId]);

  // Setup video event listeners and autoplay
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !promoVideo) return;

    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);
    const handleEnded = () => setIsPlaying(false);

    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);
    video.addEventListener('ended', handleEnded);

    // Set initial volume
    video.volume = volume;

    // Determine if viewing own profile or another user's
    const isOwner = currentUser && (currentUser.user_id === userId || currentUser.id === userId);
    
    // Auto-mute when viewing another user's profile
    if (!isOwner) {
      video.muted = true;
      setIsMuted(true);
    } else {
      video.muted = false;
      setIsMuted(false);
    }

    // Autoplay when video loads - more aggressive approach
    const attemptAutoplay = async () => {
      try {
        console.log('🎬 Attempting autoplay...');
        await video.play();
        setIsPlaying(true);
        console.log('✅ Autoplay successful');
        // Increment view count on autoplay
        if (promoVideo) {
          handleVideoViewIncrement(promoVideo.id);
        }
      } catch (error) {
        console.log('❌ Autoplay prevented:', error);
        // If autoplay fails, try again with muted
        try {
          video.muted = true;
          setIsMuted(true);
          await video.play();
          setIsPlaying(true);
          console.log('✅ Autoplay successful (muted)');
        } catch (err) {
          console.log('❌ Autoplay failed even when muted:', err);
        }
      }
    };

    // Multiple attempts to autoplay
    const tryAutoplay = () => {
      if (video.readyState >= 3) {
        // Video is ready
        attemptAutoplay();
      } else if (video.readyState >= 2) {
        // Has enough data to start
        setTimeout(attemptAutoplay, 200);
      } else {
        // Wait for video to load
        video.addEventListener('canplay', attemptAutoplay, { once: true });
      }
    };

    // Start autoplay attempt after component is fully mounted
    setTimeout(tryAutoplay, 300);

    return () => {
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
      video.removeEventListener('ended', handleEnded);
    };
  }, [promoVideo, currentUser, userId, volume]);

  // Check follow and friend status
  useEffect(() => {
    const checkRelationships = async () => {
      if (!currentUser || !userId) return;
      const viewerId = currentUser.user_id || currentUser.id;
      if (viewerId === userId) return; // Don't check for own profile

      try {
        const API = process.env.REACT_APP_BACKEND_URL || '';
        
        // Check if following
        const followingRes = await fetch(`${API}/api/users/${viewerId}/following`);
        if (followingRes.ok) {
          const following = await followingRes.json();
          console.log('👥 Following list:', following);
          console.log('🔍 Looking for userId:', userId);
          const isFollowingUser = following.some(f => f.id === userId || f.user_id === userId);
          console.log('✅ Is following?', isFollowingUser);
          setIsFollowing(isFollowingUser);
        }

        // Check friend status
        const friendsRes = await fetch(`${API}/api/users/${viewerId}/friends`);
        if (friendsRes.ok) {
          const friends = await friendsRes.json();
          console.log('👫 Friends list:', friends);
          console.log('🔍 Looking for userId:', userId);
          const isFriendUser = friends.some(f => f.id === userId || f.user_id === userId);
          console.log('✅ Is friend?', isFriendUser);
          setIsFriend(isFriendUser);
        }

        // Check pending friend requests
        const requestsRes = await fetch(`${API}/api/users/${viewerId}/friend-requests`);
        if (requestsRes.ok) {
          const requests = await requestsRes.json();
          console.log('📬 Friend requests:', requests);
          const hasPendingRequest = requests.some(r => r.to_user_id === userId && r.status === 'pending');
          console.log('⏳ Has pending request?', hasPendingRequest);
          setFriendRequestStatus(hasPendingRequest ? 'pending' : null);
        }
      } catch (err) {
        console.error('Error checking relationships:', err);
      }
    };

    checkRelationships();
  }, [userId, currentUser]);

  // Fetch top 10 tracks when Music tab is active
  useEffect(() => {
    const fetchTop10 = async () => {
      if (activeTab === 'music' && userId) {
        setLoadingTop10(true);
        try {
          const API = process.env.REACT_APP_BACKEND_URL || '';
          
          // Fetch monthly top 10
          const monthlyResponse = await fetch(`${API}/api/profile/${userId}/top-tracks?timeframe=monthly`);
          if (monthlyResponse.ok) {
            const monthlyData = await monthlyResponse.json();
            console.log('📊 Monthly Top 10 data:', monthlyData);
            setMonthlyTop10(monthlyData);
          }
          
          // Fetch all-time top 10
          const alltimeResponse = await fetch(`${API}/api/profile/${userId}/top-tracks?timeframe=all`);
          if (alltimeResponse.ok) {
            const alltimeData = await alltimeResponse.json();
            console.log('📊 All-time Top 10 data:', alltimeData);
            setAlltimeTop10(alltimeData);
          }
        } catch (err) {
          console.error('Error fetching top 10:', err);
        } finally {
          setLoadingTop10(false);
        }
      }
    };

    fetchTop10();
  }, [activeTab, userId]);

  // Check if profile needs completion (show banner)
  useEffect(() => {
    if (profileData?.user && currentUser && (currentUser.user_id === userId || currentUser.id === userId)) {
      // Check if bio or user_type is missing
      const needsCompletion = !profileData.user.bio || !profileData.user.user_type || profileData.user.user_type === 'regular';
      const bannerDismissed = localStorage.getItem(`profile_completion_banner_dismissed_${userId}`);
      
      if (needsCompletion && !bannerDismissed) {
        setShowCompletionBanner(true);
      }
    }
  }, [profileData, currentUser, userId]);

  const handleFollow = async () => {
    // TODO: Implement follow/unfollow functionality
    console.log('Follow button clicked');
  };

  const handleWhisper = () => {
    // Open the local WhisperModal (rendered at the end of this component) — works on /profile route
    // where ElectroShazam (and its global whisper listener) is unmounted
    if (!profileData || !profileData.user) return;
    setShowLocalWhisper(true);
  };

  // Fetch emotes for the WhisperModal (one-time, when component mounts)
  useEffect(() => {
    const API = process.env.REACT_APP_BACKEND_URL || '';
    const token = localStorage.getItem('beatseek_token');
    if (!token) return;
    const headers = { 'Authorization': `Bearer ${token}` };
    fetch(`${API}/api/emotes/global`, { headers })
      .then(r => r.ok ? r.json() : [])
      .then(d => setGlobalEmotes(Array.isArray(d) ? d : (d.emotes || [])))
      .catch(() => {});
    fetch(`${API}/api/emotes/my-custom`, { headers })
      .then(r => r.ok ? r.json() : [])
      .then(d => setCustomEmotes(Array.isArray(d) ? d : (d.emotes || [])))
      .catch(() => {});
  }, []);

  const handleShare = () => {
    const profileUrl = window.location.href;
    navigator.clipboard.writeText(profileUrl).then(() => {
      alert('Profile link copied to clipboard!');
    }).catch(err => {
      console.error('Failed to copy:', err);
    });
  };

  const handleAddFriend = async () => {
    if (friendRequestStatus === 'pending') return;
    
    // If already friends, confirm before removing friendship
    if (isFriend) {
      const confirmed = window.confirm('Are you sure you want to remove this friend?');
      if (!confirmed) return;
    }
    
    try {
      const API = process.env.REACT_APP_BACKEND_URL || '';
      const viewerId = currentUser?.user_id || currentUser?.id;
      const token = localStorage.getItem('beatseek_token');
      
      if (!viewerId) {
        alert('Please log in to manage friends');
        return;
      }

      // If already friends, unfriend (remove from friends)
      if (isFriend) {
        const response = await fetch(`${API}/api/users/${viewerId}/unfriend/${userId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          setIsFriend(false);
          setIsFollowing(false); // Also update following state since unfriend removes follow
          alert('Friend removed successfully');
        } else {
          const error = await response.json();
          alert(`Failed to remove friend: ${error.detail || 'Unknown error'}`);
        }
      } else {
        // Send friend request
        const response = await fetch(`${API}/api/users/${viewerId}/friend-request`, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ to_user_id: userId })
        });

        if (response.ok) {
          setFriendRequestStatus('pending');
          alert('Friend request sent! They will need to accept your request.');
        } else {
          const error = await response.json();
          alert(`Failed to send friend request: ${error.detail || 'Unknown error'}`);
        }
      }
    } catch (err) {
      console.error('Error managing friendship:', err);
      alert('Failed to manage friendship');
    }
  };

  // State for button hover
  const [followButtonHovered, setFollowButtonHovered] = useState(false);
  const [friendButtonHovered, setFriendButtonHovered] = useState(false);

  const handleFollowUser = async (targetUserId) => {
    // If already following, confirm before unfollowing
    if (isFollowing) {
      const confirmed = window.confirm('Are you sure you want to unfollow this user?');
      if (!confirmed) return;
    }
    
    try {
      const API = process.env.REACT_APP_BACKEND_URL || '';
      const viewerId = currentUser?.user_id || currentUser?.id;
      
      if (!viewerId) {
        alert('Please log in to follow users');
        return;
      }

      // Toggle follow/unfollow
      if (isFollowing) {
        // Unfollow
        const response = await fetch(`${API}/api/users/${viewerId}/follow/${targetUserId}`, {
          method: 'DELETE'
        });

        if (response.ok) {
          setIsFollowing(false);
          
          // Refresh community data if on community tab
          if (activeTab === 'community') {
            const url = `${API}/api/profile/${userId}/community?viewer_id=${viewerId}`;
            const refreshResponse = await fetch(url);
            if (refreshResponse.ok) {
              const data = await refreshResponse.json();
              setCommunityData(data);
            }
          }
        }
      } else {
        // Follow
        const response = await fetch(`${API}/api/users/${viewerId}/follow`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ followed_user_id: targetUserId })
        });

        if (response.ok) {
          setIsFollowing(true);
          
          // Refresh community data if on community tab
          if (activeTab === 'community') {
            const url = `${API}/api/profile/${userId}/community?viewer_id=${viewerId}`;
            const refreshResponse = await fetch(url);
            if (refreshResponse.ok) {
              const data = await refreshResponse.json();
              setCommunityData(data);
            }
          }
        }
      }
    } catch (err) {
      console.error('Error toggling follow:', err);
    }
  };

  const handleUnfollowUser = async (targetUserId) => {
    try {
      const API = process.env.REACT_APP_BACKEND_URL || '';
      const viewerId = currentUser?.user_id || currentUser?.id;
      
      if (!viewerId) {
        return;
      }

      const response = await fetch(`${API}/api/users/${viewerId}/follow/${targetUserId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        // Update local state if unfollowing profile owner
        if (targetUserId === userId) {
          setIsFollowing(false);
        }
        
        // Refresh community data if on community tab
        if (activeTab === 'community') {
          const url = `${API}/api/profile/${userId}/community?viewer_id=${viewerId}`;
          const refreshResponse = await fetch(url);
          if (refreshResponse.ok) {
            const data = await refreshResponse.json();
            setCommunityData(data);
          }
        }
      }
    } catch (err) {
      console.error('Error unfollowing user:', err);
    }
  };

  // Format timestamp for activity
  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  // Check if user is recently active (last 5 minutes)
  const isRecentlyActive = (lastSeen) => {
    if (!lastSeen) return false;
    // last_seen is stored in UTC; if the string carries no timezone, treat it as UTC
    // (otherwise the browser parses it as local time and the dot shows wrong).
    let val = lastSeen;
    if (typeof val === 'string' && !/([zZ]|[+-]\d{2}:?\d{2})$/.test(val)) {
      val = val.replace(' ', 'T') + 'Z';
    }
    const lastSeenDate = new Date(val);
    if (isNaN(lastSeenDate.getTime())) return false;
    const diffMins = (Date.now() - lastSeenDate.getTime()) / 60000;
    return diffMins >= 0 && diffMins < 5;
  };

  // Check if activity is within last 7 days
  const isWithin7Days = (timestamp) => {
    if (!timestamp) return false;
    const activityDate = new Date(timestamp);
    const now = new Date();
    const diffMs = now - activityDate;
    const diffDays = Math.floor(diffMs / 86400000);
    return diffDays <= 7;
  };

  // Format last seen
  const formatLastSeen = (lastSeen) => {
    if (!lastSeen) return 'Unknown';
    const date = new Date(lastSeen);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 5) return 'Active now';
    if (diffMins < 60) return `Active ${diffMins}m ago`;
    if (diffHours < 24) return `Active ${diffHours}h ago`;
    if (diffDays === 1) return 'Active yesterday';
    if (diffDays < 7) return `Active ${diffDays}d ago`;
    return `Last seen ${date.toLocaleDateString()}`;
  };

  // Handle "Go to" navigation for activity items
  const handleGoToActivity = (activity) => {
    if (activity.type === 'track_identified' || activity.type === 'track_shared') {
      alert('Coming soon! Track details page is under development.');
      return;
    }

    if (activity.type === 'chat_message') {
      const room = activity.data.room || 'global';
      const messageId = activity.data.message_id;
      
      // Navigate to chat with room and highlight message
      navigate('/', { 
        state: { 
          openChat: true, 
          chatRoom: room,
          highlightMessageId: messageId 
        } 
      });
      return;
    }

    if (activity.type === 'forum_post') {
      const postId = activity.data.post_id;
      if (postId) {
        // Navigate to forum post (assuming route exists)
        navigate(`/forum/post/${postId}`);
      } else {
        alert('Coming soon! Forum post navigation is under development.');
      }
      return;
    }
  };

  // Open View All modal
  const openViewAllModal = (section, users) => {
    setViewAllModal({ isOpen: true, section, users });
  };

  // Close View All modal
  const closeViewAllModal = () => {
    setViewAllModal({ isOpen: false, section: null, users: [] });
  };

  // Video Handlers
  const handleVideoUploadSuccess = (newVideo) => {
    setPromoVideo(newVideo);
    setShowUploadModal(false);
  };

  const handleVideoEdit = (video) => {
    setVideoToEdit(video);
    setShowEditModal(true);
  };

  const handleVideoEditSuccess = (updatedVideo) => {
    setPromoVideo(updatedVideo);
    setShowEditModal(false);
    setVideoToEdit(null);
  };

  const handleVideoDelete = async (videoId) => {
    if (!window.confirm('Are you sure you want to delete this video? This action cannot be undone.')) {
      return;
    }

    try {
      const API = process.env.REACT_APP_BACKEND_URL || '';
      const token = localStorage.getItem('beatseek_token');

      const response = await fetch(`${API}/api/promo-videos/${videoId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        setPromoVideo(null);
        alert('Video deleted successfully!');
      } else {
        const errorData = await response.json();
        alert(`Failed to delete video: ${errorData.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error deleting video:', error);
      alert('Failed to delete video. Please try again.');
    }
  };

  const handleVideoViewIncrement = async (videoId) => {
    try {
      const API = process.env.REACT_APP_BACKEND_URL || '';
      const token = localStorage.getItem('beatseek_token');

      await fetch(`${API}/api/promo-videos/${videoId}/view`, {
        method: 'POST',
        headers: {
          'Authorization': token ? `Bearer ${token}` : '',
          'Content-Type': 'application/json'
        }
      });
    } catch (error) {
      console.error('Error incrementing view count:', error);
    }
  };

  const handleVideoLike = async () => {
    if (!currentUser || !promoVideo) return;

    try {
      const API = process.env.REACT_APP_BACKEND_URL || '';
      const token = localStorage.getItem('beatseek_token');

      const response = await fetch(`${API}/api/promo-videos/${promoVideo.id}/like`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setIsLiked(data.liked);
        setLikeCount(prev => data.liked ? prev + 1 : prev - 1);
      }
    } catch (error) {
      console.error('Error liking video:', error);
    }
  };

  // Compact Video Player Functions
  const togglePlay = async () => {
    if (videoRef.current) {
      try {
        if (isPlaying) {
          await videoRef.current.pause();
          setIsPlaying(false);
        } else {
          await videoRef.current.play();
          setIsPlaying(true);
        }
      } catch (error) {
        console.error('Video playback error:', error);
        setIsPlaying(false);
      }
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      const newMutedState = !isMuted;
      videoRef.current.muted = newMutedState;
      setIsMuted(newMutedState);
    }
  };

  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    if (videoRef.current) {
      videoRef.current.volume = newVolume;
      if (newVolume === 0) {
        setIsMuted(true);
        videoRef.current.muted = true;
      } else if (isMuted) {
        setIsMuted(false);
        videoRef.current.muted = false;
      }
    }
  };

  const handleMouseMove = () => {
    setShowControls(true);
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }
    if (isPlaying) {
      controlsTimeoutRef.current = setTimeout(() => {
        setShowControls(false);
      }, 3000);
    }
  };

  // Promo handlers
  const handleDeletePromo = async (promoId) => {
    if (!window.confirm('Are you sure you want to delete this promo?')) {
      return;
    }

    try {
      const API = process.env.REACT_APP_BACKEND_URL || '';
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${API}/api/promos/${promoId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        // Remove from local state
        setPromosData(promosData.filter(p => p.id !== promoId));
        alert('Promo deleted successfully!');
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to delete promo');
      }
    } catch (error) {
      console.error('Error deleting promo:', error);
      alert('An error occurred while deleting the promo');
    }
  };

  const handleEditPromo = (promo) => {
    setPromoToEdit(promo);
    setShowEditPromoModal(true);
  };

  const handleUpdatePromo = async (promoId, updatedData) => {
    try {
      const API = process.env.REACT_APP_BACKEND_URL || '';
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${API}/api/promos/${promoId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(updatedData)
      });

      if (response.ok) {
        const result = await response.json();
        // Update local state
        setPromosData(promosData.map(p => p.id === promoId ? result.promo : p));
        setShowEditPromoModal(false);
        setPromoToEdit(null);
        alert('Promo updated successfully!');
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to update promo');
      }
    } catch (error) {
      console.error('Error updating promo:', error);
      alert('An error occurred while updating the promo');
    }
  };

  const handleDeleteGear = async (gearId, gearName) => {
    if (!window.confirm(`Remove "${gearName}" from your gear collection?`)) {
      return;
    }
    
    try {
      const API = process.env.REACT_APP_BACKEND_URL || '';
      const token = localStorage.getItem('beatseek_token') || localStorage.getItem('token');
      
      const response = await fetch(`${API}/api/gear/user/collection/${encodeURIComponent(gearId)}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        // Remove from local state
        setUserGear(userGear.filter(g => g.gear_id !== gearId));
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to remove gear');
      }
    } catch (error) {
      console.error('Error deleting gear:', error);
      alert('An error occurred while removing the gear');
    }
  };

  // Artist follow management functions
  const handleUnfollowArtist = async (artistName) => {
    const token = localStorage.getItem('beatseek_token') || localStorage.getItem('token');
    if (!token) return;

    try {
      const API = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(
        `${API}/api/artists/follow/${encodeURIComponent(artistName)}`,
        {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (response.ok) {
        setFollowedArtists(prev => prev.filter(a => a.artist_name !== artistName));
      }
    } catch (err) {
      console.error('Error unfollowing artist:', err);
    }
  };

  const handleToggleArtistNotifications = async (artistName, currentState) => {
    const token = localStorage.getItem('beatseek_token') || localStorage.getItem('token');
    if (!token) return;

    try {
      const API = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(
        `${API}/api/artists/follow/${encodeURIComponent(artistName)}/notifications`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ enabled: !currentState })
        }
      );

      if (response.ok) {
        setFollowedArtists(prev => 
          prev.map(a => 
            a.artist_name === artistName 
              ? { ...a, notifications_enabled: !currentState }
              : a
          )
        );
      }
    } catch (err) {
      console.error('Error toggling notifications:', err);
    }
  };

  const formatFollowDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  const handleShareGearToDiscord = (gear) => {
    // Check if Discord is connected
    const discordToken = localStorage.getItem('discord_access_token');
    if (!discordToken) {
      alert('Please connect your Discord account to use this feature!\n\nGo to My Dashboard → Discord Integration → Connect with Discord');
      return;
    }
    
    setDiscordShareData({
      name: gear.gear_name,
      title: gear.gear_name,
      brand: gear.gear_brand,
      category: gear.gear_category,
      rating: gear.user_rating,
      review: gear.user_review,
      image: gear.image_url,
      url: gear.source_url
    });
    setShowDiscordShareModal(true);
  };

  const isOwner = currentUser && (currentUser.user_id === userId || currentUser.id === userId);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <div className="text-white text-xl animate-pulse">Loading profile...</div>
      </div>
    );
  }

  if (error || !profileData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <div className="bg-slate-800 rounded-xl p-8 border border-red-500/30 max-w-md text-center">
          <div className="text-6xl mb-4">😕</div>
          <h2 className="text-2xl font-bold text-white mb-2">Profile Not Found</h2>
          <p className="text-gray-400 mb-6">
            {error || 'The profile you are looking for does not exist.'}
          </p>
          <button
            onClick={() => navigate('/')}
            className="bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white px-6 py-3 rounded-lg transition-all font-medium"
          >
            Go to Home
          </button>
        </div>
      </div>
    );
  }

  const { user, stats, relationship, recent_activity, music } = profileData;
  const API = process.env.REACT_APP_BACKEND_URL || '';
  const avatarUrl = user.profile_picture 
    ? (user.profile_picture.startsWith('http') 
        ? user.profile_picture 
        : `${API}${user.profile_picture.replace('/uploads/', '/api/uploads/')}`)
    : null;

  // Format member since date
  const memberSince = user.joined_date 
    ? new Date(user.joined_date).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
    : 'Unknown';

  // Construct location string
  let locationString = '';
  if (user.city || user.state || user.country) {
    const parts = [];
    if (user.city) parts.push(user.city);
    if (user.state) parts.push(user.state);
    if (user.country) parts.push(user.country);
    locationString = parts.join(', ');
  }

  // Tab configuration
  const tabs = [
    { id: 'overview', label: 'Overview', gradient: 'from-purple-600 to-pink-600' },
    { id: 'music', label: 'Music', gradient: 'from-cyan-600 to-blue-600' },
    { id: 'gear', label: 'My Gear', gradient: 'from-amber-600 to-yellow-600' },
    { id: 'activity', label: 'Social Activity', gradient: 'from-green-600 to-emerald-600' },
    { id: 'community', label: 'Community', gradient: 'from-orange-600 to-yellow-600' },
    { id: 'badges', label: 'Badges', gradient: 'from-red-600 to-orange-600' },
    { id: 'promos', label: 'Promos', gradient: 'from-pink-600 to-red-600' },
    { id: 'marketplace', label: 'Marketplace', gradient: 'from-cyan-600 to-purple-600' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 relative overflow-hidden">
      {/* Animated background waves */}
      <div className="absolute inset-0 opacity-30">
        <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/10 via-purple-500/10 to-pink-500/10 animate-pulse" />
      </div>

      <div className="relative z-10 py-8 px-4 max-w-6xl mx-auto">
        {/* Back Button */}
        <div className="mb-6 flex items-center gap-4">
          <button
            onClick={() => navigate('/?tab=social')}
            className="text-cyan-300 hover:text-cyan-200 flex items-center gap-2 transition-colors group"
          >
            <ArrowLeft size={20} className="group-hover:-translate-x-1 transition-transform" />
            <span className="font-medium">Back to BEATSEEK</span>
          </button>
          
          {cameFromGlobe && (
            <>
              <span className="text-gray-600">|</span>
              
              <button
                onClick={() => navigate('/?openGlobe=true')}
                className="text-purple-300 hover:text-purple-200 flex items-center gap-2 transition-colors group"
              >
                <span className="text-xl group-hover:scale-110 transition-transform">🌍</span>
                <span className="font-medium">Back to Globe</span>
              </button>
            </>
          )}
        </div>

        {/* Hero Section */}
        <div className="bg-gradient-to-br from-slate-800/90 via-slate-900/90 to-slate-800/90 backdrop-blur-xl rounded-3xl border border-cyan-500/30 shadow-2xl overflow-hidden mb-6">
          {/* Waveform Background */}
          <div className="h-48 bg-gradient-to-r from-purple-600 via-pink-600 to-purple-600 relative overflow-hidden">
            <div className="absolute inset-0 opacity-30 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPjxkZWZzPjxwYXR0ZXJuIGlkPSJncmlkIiB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHBhdHRlcm5Vbml0cz0idXNlclNwYWNlT25Vc2UiPjxwYXRoIGQ9Ik0gNDAgMCBMIDAgMCAwIDQwIiBmaWxsPSJub25lIiBzdHJva2U9IndoaXRlIiBzdHJva2Utd2lkdGg9IjEiIG9wYWNpdHk9IjAuMSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0idXJsKCNncmlkKSIvPjwvc3ZnPg==')]" />
          </div>

          <div className="relative px-8 pb-8">
            {/* Avatar with Presence Dot */}
            <div className="profile-avatar-wrap absolute -top-20 left-8">
              <div className="relative">
                <div className="profile-avatar-img w-40 h-40 rounded-full bg-gradient-to-br from-cyan-600 to-blue-600 flex items-center justify-center text-6xl font-bold text-white overflow-hidden border-4 border-slate-800 shadow-2xl">
                  {avatarUrl && !avatarFailed ? (
                    <img
                      src={avatarUrl}
                      alt={user.name}
                      className="w-full h-full object-cover"
                      onError={() => setAvatarFailed(true)}
                    />
                  ) : (
                    user.name?.charAt(0).toUpperCase() || '?'
                  )}
                </div>
                {/* Presence Dot — green pulse when online, gray static when offline. Own profile is always online. */}
                {(() => {
                  const isOwnProfile = currentUser && (currentUser.user_id === userId || currentUser.id === userId);
                  const isOnline = isOwnProfile || isRecentlyActive(user.last_seen);
                  return (
                    <div
                      data-testid="profile-presence-dot"
                      data-online={isOnline ? 'true' : 'false'}
                      className={`absolute bottom-2 right-2 w-6 h-6 border-4 border-slate-800 rounded-full ${
                        isOnline
                          ? 'bg-green-500 animate-pulse shadow-[0_0_10px_rgba(34,197,94,0.7)]'
                          : 'bg-gray-500'
                      }`}
                      title={isOnline ? 'Online' : 'Offline'}
                    />
                  );
                })()}
              </div>
            </div>

            {/* User Info and Video Section */}
            <div className="pt-24 flex flex-col lg:flex-row lg:items-start lg:justify-between gap-0">
              {/* Left Side - User Info */}
              <div className="flex-1">
                {/* Username with Admin Badge */}
                <div className="flex items-center gap-3 mb-2 flex-wrap">
                  <h1 className="text-4xl md:text-5xl font-bold text-white">{user.name}</h1>
                  <IdHunterMedal userId={user.id} size="lg" />
                  <HunterChip userId={user.id} />
                  <UserBadgeGroup
                    badges={
                      user.role === 'admin'
                        ? ['admin', ...((user.badges) || []).filter(b => b !== 'original_40' && b !== 'admin')]
                        : ((user.badges) || [])
                    }
                    size="md"
                  />
                </div>

                {/* User Type Badges (DJ, Producer, Streamer, Label, A&R) - Subtle styling */}
                {user.user_type && user.user_type !== 'regular' && (
                  <div className="flex flex-wrap gap-3 mb-3">
                    {(Array.isArray(user.user_type) ? user.user_type.includes('dj') : user.user_type.includes('dj')) && (
                      <span className="text-purple-400 text-sm font-semibold flex items-center gap-1.5">
                        🎧 DJ
                      </span>
                    )}
                    {(Array.isArray(user.user_type) ? user.user_type.includes('producer') : user.user_type.includes('producer')) && (
                      <span className="text-cyan-400 text-sm font-semibold flex items-center gap-1.5">
                        🎹 Producer
                      </span>
                    )}
                    {(Array.isArray(user.user_type) ? user.user_type.includes('streamer') : user.user_type.includes('streamer')) && (
                      <span className="text-pink-400 text-sm font-semibold flex items-center gap-1.5">
                        📡 Streamer
                      </span>
                    )}
                    {(Array.isArray(user.user_type) ? user.user_type.includes('label') : user.user_type.includes('label')) && (
                      <span className="text-amber-400 text-sm font-semibold flex items-center gap-1.5">
                        💿 Record Label
                      </span>
                    )}
                    {(Array.isArray(user.user_type) ? user.user_type.includes('ar') : user.user_type.includes('ar')) && (
                      <span className="text-fuchsia-400 text-sm font-semibold flex items-center gap-1.5">
                        🎤 A&R
                      </span>
                    )}
                  </div>
                )}

                {/* Mutual Friends Hint Line */}
                {stats.mutual_friends_count > 0 && (currentUser?.user_id || currentUser?.id) !== userId && (
                  <div className="mb-3">
                    <span
                      onClick={() => setShowMutualModal(true)}
                      data-testid="profile-mutual-friends-hint"
                      title="View mutual friends"
                      className="text-cyan-400 text-sm cursor-pointer hover:text-cyan-300 transition-colors"
                    >
                      🤝 {stats.mutual_friends_count} mutual {stats.mutual_friends_count === 1 ? 'friend' : 'friends'}
                      {profileData.mutual_friends?.length > 0 && ` · ${profileData.mutual_friends.slice(0, 2).map(f => f.name).join(', ')}`}
                      <span className="text-white font-semibold hover:underline"> · View</span>
                    </span>
                  </div>
                )}

                {/* Availability - Clickable text that opens email */}
                {(user.open_for_bookings || user.open_for_collabs || user.accepting_demos) && (
                  <div className="flex flex-wrap gap-4 mb-3">
                    {user.open_for_bookings && (
                      <a
                        href={`mailto:${user.email}?subject=${encodeURIComponent('BeatSeek Booking Inquiry')}&body=${encodeURIComponent(`Hi ${user.name},\n\nI found your profile on BeatSeek and I'm interested in booking you for an event.\n\nEvent Details:\n- Date: \n- Venue: \n- Set Length: \n- Genre/Style: \n\nPlease let me know your availability and rates.\n\nThanks!`)}`}
                        className="text-white text-sm font-medium flex items-center gap-1.5 hover:text-fuchsia-400 transition-colors cursor-pointer"
                      >
                        📅 Available for Bookings
                      </a>
                    )}
                    {user.open_for_collabs && (
                      <a
                        href={`mailto:${user.email}?subject=${encodeURIComponent('BeatSeek Collaboration Request')}&body=${encodeURIComponent(`Hi ${user.name},\n\nI found your profile on BeatSeek and I'd love to collaborate with you!\n\nAbout me:\n- \n\nCollaboration idea:\n- \n\nLet me know if you're interested!\n\nCheers!`)}`}
                        className="text-white text-sm font-medium flex items-center gap-1.5 hover:text-fuchsia-400 transition-colors cursor-pointer"
                      >
                        🤝 Open for Collabs
                      </a>
                    )}
                    {user.accepting_demos && (
                      <a
                        href={`mailto:${user.demo_submission_email || user.email}?subject=${encodeURIComponent('Demo Submission')}&body=${encodeURIComponent(`Hi ${user.name},\n\nI'd like to submit a demo for your consideration.\n\nArtist Name:\nTrack Title:\nGenre:\nSoundCloud/Streaming Link:\n\nBrief Description:\n\n\nThank you for your time!`)}`}
                        className="text-amber-400 text-sm font-medium flex items-center gap-1.5 hover:text-amber-300 transition-colors cursor-pointer"
                      >
                        📬 Accepting Demos
                      </a>
                    )}
                  </div>
                )}

                {/* Contact Button for Bookings/Collabs - Hidden since text links now handle this */}

                {/* Location */}
                {locationString && (
                  <p className="text-gray-400 text-lg mb-2 flex items-center gap-2">
                    📍 {locationString}
                  </p>
                )}

                {/* Member Since */}
                <p className="text-sm text-gray-500">
                  Member since {memberSince}
                </p>
              </div>

              {/* Right Side - Promo Video (Slightly larger, positioned higher) */}
              {(promoVideo || (user.user_type && user.user_type !== 'regular' && isOwner)) && (
                <div className="profile-promo-video-section w-full lg:w-[480px] lg:min-w-[480px] lg:max-w-[480px] flex-shrink-0 lg:-mt-20 lg:-mr-3">
                  {promoVideo ? (
                    <div className="bg-gradient-to-br from-slate-800/70 via-slate-900/70 to-slate-800/70 backdrop-blur-xl rounded-xl border border-purple-500/30 shadow-xl overflow-hidden" style={{ width: '480px', maxWidth: '480px' }}>
                      {/* Compact Video Player */}
                      <div 
                        className="relative bg-black overflow-hidden cursor-pointer"
                        style={{ width: '480px', height: '270px', maxWidth: '480px', maxHeight: '270px' }}
                        onClick={(e) => {
                          // Only toggle play if clicking on the video itself, not controls
                          if (e.target === e.currentTarget || e.target.tagName === 'VIDEO') {
                            togglePlay();
                          }
                        }}
                        onMouseMove={handleMouseMove}
                        onMouseEnter={() => {
                          setIsHovering(true);
                          setShowControls(true);
                        }}
                        onMouseLeave={() => {
                          setIsHovering(false);
                          if (isPlaying) {
                            setShowControls(false);
                          }
                        }}
                      >
                        <video
                          ref={videoRef}
                          src={promoVideo?.video_url 
                            ? (promoVideo.video_url.startsWith('http') 
                                ? promoVideo.video_url 
                                : `${API}${promoVideo.video_url}`)
                            : null}
                          poster={promoVideo?.thumbnail_url 
                            ? (promoVideo.thumbnail_url.startsWith('http') 
                                ? promoVideo.thumbnail_url 
                                : `${API}${promoVideo.thumbnail_url}`)
                            : null}
                          className="w-full h-full object-cover"
                          playsInline
                          muted
                          preload="metadata"
                          loop
                        />

                        {/* Compact Play/Pause Overlay */}
                        {!isPlaying && (
                          <div 
                            className="absolute inset-0 flex items-center justify-center bg-black/30 cursor-pointer z-10"
                            onClick={togglePlay}
                          >
                            <div className="bg-cyan-500/90 hover:bg-cyan-600 rounded-full p-4 transition-all transform hover:scale-110">
                              <Play size={32} className="text-white ml-0.5" fill="white" />
                            </div>
                          </div>
                        )}

                        {/* Volume Control - Icon always visible, slider toggles open on click (TOP-right) */}
                        <div className="promo-video-volume absolute top-2 right-2 flex items-center gap-2 bg-black/70 rounded-lg px-2 py-1.5 z-20">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setVolumeExpanded((v) => !v);
                            }}
                            className="text-white hover:text-cyan-400 transition-colors z-20"
                            title={volumeExpanded ? 'Hide volume' : 'Show volume'}
                          >
                            {isMuted || volume === 0 ? <VolumeX size={16} /> : <Volume2 size={16} />}
                          </button>
                          {volumeExpanded && (
                            <>
                              <button
                                onClick={(e) => { e.stopPropagation(); toggleMute(); }}
                                className="text-xs text-cyan-400 hover:text-cyan-200 transition-colors z-20"
                              >
                                {isMuted ? 'Unmute' : 'Mute'}
                              </button>
                              <input
                                type="range"
                                min="0"
                                max="1"
                                step="0.01"
                                value={volume}
                                onChange={handleVolumeChange}
                                onClick={(e) => e.stopPropagation()}
                                className="w-16 h-1 bg-gray-600 rounded-lg appearance-none cursor-pointer z-20"
                                style={{
                                  background: `linear-gradient(to right, #22d3ee 0%, #22d3ee ${volume * 100}%, #4b5563 ${volume * 100}%, #4b5563 100%)`
                                }}
                              />
                            </>
                          )}
                        </div>

                        {/* Mobile stats overlay — views + likes (+ edit/delete for owner) at BOTTOM-right of video */}
                        <div className="promo-video-mobile-stats absolute bottom-2 right-2 flex items-center gap-2 bg-black/70 rounded-lg px-2 py-1.5 z-20 text-xs">
                          <div className="flex items-center gap-1 text-gray-300">
                            <Eye size={12} />
                            <span>{promoVideo.view_count || 0}</span>
                          </div>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleVideoLike(); }}
                            className={`flex items-center gap-1 transition-colors ${
                              isLiked ? 'text-pink-400' : 'text-gray-300 hover:text-pink-400'
                            }`}
                          >
                            <ThumbsUp size={12} fill={isLiked ? 'currentColor' : 'none'} />
                            <span>{likeCount}</span>
                          </button>
                          {isOwner && (
                            <>
                              <button
                                onClick={(e) => { e.stopPropagation(); handleVideoEdit(promoVideo); }}
                                className="text-cyan-400 hover:text-cyan-200 transition-colors"
                                title="Edit video"
                              >
                                <Edit2 size={12} />
                              </button>
                              <button
                                onClick={(e) => { e.stopPropagation(); handleVideoDelete(promoVideo.id); }}
                                className="text-red-400 hover:text-red-300 transition-colors"
                                title="Delete video"
                              >
                                <Trash2 size={12} />
                              </button>
                            </>
                          )}
                        </div>
                      </div>

                      {/* Compact Footer - Reduced padding */}
                      <div className="p-1.5 border-t border-gray-700/50 flex items-center justify-between text-xs">
                        <div className="flex items-center gap-3 text-gray-400">
                          <div className="flex items-center gap-1">
                            <Eye size={12} />
                            <span>{promoVideo.view_count || 0}</span>
                          </div>
                          <button 
                            onClick={handleVideoLike}
                            className={`flex items-center gap-1 transition-colors ${
                              isLiked ? 'text-pink-400' : 'text-gray-400 hover:text-pink-400'
                            }`}
                          >
                            <ThumbsUp size={12} fill={isLiked ? 'currentColor' : 'none'} />
                            <span>{likeCount}</span>
                          </button>
                        </div>
                        {isOwner && (
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => handleVideoEdit(promoVideo)}
                              className="bg-slate-700 hover:bg-slate-600 text-cyan-400 px-2 py-1 rounded transition-all text-xs font-medium"
                            >
                              <Edit2 size={12} />
                            </button>
                            <button
                              onClick={() => handleVideoDelete(promoVideo.id)}
                              className="bg-red-500/20 hover:bg-red-500/30 text-red-400 border border-red-500/50 px-2 py-1 rounded transition-all text-xs font-medium"
                            >
                              <Trash2 size={12} />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <>
                      {currentUser && (currentUser.user_id === userId || currentUser.id === userId) && (
                        <div className="bg-gradient-to-br from-slate-700/50 via-purple-900/20 to-slate-700/50 backdrop-blur-xl rounded-xl border border-purple-500/30 p-6 text-center">
                          <Video className="mx-auto mb-3 text-purple-400" size={36} />
                          <h4 className="text-base font-bold text-white mb-2">Showcase Your Work</h4>
                          <p className="text-gray-400 text-xs mb-3">
                            Upload a promo video!
                          </p>
                          <button
                            onClick={() => setShowUploadModal(true)}
                            className="bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white px-4 py-2 rounded-lg transition-all font-medium inline-flex items-center gap-2 text-sm"
                          >
                            <Video size={16} />
                            Upload Video
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Action Buttons - Positioned higher in hero section */}
            {currentUser && currentUser.user_id !== userId && currentUser.id !== userId && (
              <div className="profile-actions-row absolute top-[-55px] right-6 flex flex-wrap gap-2 z-20">
                <button
                  onClick={handleAddFriend}
                  onMouseEnter={() => setFriendButtonHovered(true)}
                  onMouseLeave={() => setFriendButtonHovered(false)}
                  disabled={friendRequestStatus === 'pending'}
                  title={isFriend ? 'Remove Friend' : friendRequestStatus === 'pending' ? 'Friend Request Sent' : 'Add Friend'}
                  className={`px-3 py-1 rounded-lg text-xs font-semibold transition-all shadow-lg flex items-center gap-1.5 ${
                    isFriend
                      ? 'bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white'
                      : friendRequestStatus === 'pending'
                      ? 'bg-yellow-600/50 text-yellow-200 border border-yellow-500/50 cursor-not-allowed'
                      : 'bg-gradient-to-r from-pink-600/60 to-purple-600/60 hover:from-pink-600/80 hover:to-purple-600/80 border border-pink-500/50 text-white'
                  }`}
                >
                  {isFriend && friendButtonHovered ? <UserMinus size={14} /> : <UserPlus size={14} />}
                  {isFriend && friendButtonHovered ? 'Remove Friend' : isFriend ? 'Friends ✓' : friendRequestStatus === 'pending' ? 'Sent' : 'Add Friend'}
                </button>

                <button
                  onClick={() => handleFollowUser(userId)}
                  onMouseEnter={() => setFollowButtonHovered(true)}
                  onMouseLeave={() => setFollowButtonHovered(false)}
                  title={isFollowing ? 'Unfollow' : 'Follow'}
                  className={`${
                    isFollowing
                      ? 'bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500'
                      : 'bg-gradient-to-r from-pink-600/60 to-purple-600/60 hover:from-pink-600/80 hover:to-purple-600/80 border border-pink-500/50'
                  } text-white px-3 py-1 rounded-lg transition-all text-xs font-semibold flex items-center gap-1.5 shadow-lg`}
                >
                  <Heart size={14} fill={isFollowing ? 'white' : 'none'} />
                  {isFollowing && followButtonHovered ? 'Unfollow' : isFollowing ? 'Following' : 'Follow'}
                </button>

                <button
                  onClick={handleWhisper}
                  title="Send Whisper"
                  className="bg-gradient-to-r from-pink-600/60 to-purple-600/60 hover:from-pink-600/80 hover:to-purple-600/80 border border-pink-500/50 text-white px-3 py-1 rounded-lg transition-all text-xs font-semibold flex items-center gap-1.5 shadow-lg"
                >
                  <WhisperIcon size={28} />
                  Whisper
                </button>

                <button
                  onClick={handleShare}
                  title="Share Profile"
                  className="bg-gradient-to-r from-pink-600/60 to-purple-600/60 hover:from-pink-600/80 hover:to-purple-600/80 border border-pink-500/50 text-white px-3 py-1 rounded-lg transition-all text-xs font-semibold flex items-center gap-1.5 shadow-lg"
                >
                  <Share2 size={14} />
                  Share
                </button>

              </div>
            )}

            {/* Artist Notification Bell - only for DJ/Producer/Streamer — pinned bottom-right of hero so it never disrupts action buttons layout */}
            {currentUser && currentUser.user_id !== userId && currentUser.id !== userId && user && ['dj', 'producer', 'streamer'].some(type => 
              user.user_type?.toLowerCase().includes(type)
            ) && (
              <div className="profile-artist-bell absolute bottom-3 right-3 z-20">
                <ArtistNotificationBell 
                  artistName={user.name}
                  artistId={userId}
                  source="beatseek"
                  artworkUrl={user.profile_picture ? `${process.env.REACT_APP_BACKEND_URL}${user.profile_picture.replace('/uploads/', '/api/uploads/')}` : null}
                  size="large"
                  showEmailAlert={true}
                />
              </div>
            )}

            {/* Owner Action Buttons - Edit Profile + Share Profile */}
            {currentUser && (currentUser.user_id === userId || currentUser.id === userId) && (
              <div className="profile-actions-row absolute top-[-55px] right-6 flex flex-wrap items-center gap-2 z-20">
                <ShareProfileButton userId={userId} userName={user.name} />
                <button
                  onClick={() => setShowEditProfileModal(true)}
                  className="bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white px-3 py-1 rounded-lg transition-all text-xs font-semibold flex items-center gap-1.5 shadow-lg"
                >
                  <Edit2 size={14} />
                  Edit Profile
                </button>
              </div>
            )}

            {/* Social Links Row */}
            <div className="profile-social-links-row flex flex-wrap items-start justify-between gap-4 mt-6">
              {/* Social Links - Left Side */}
              <div className="profile-social-links flex flex-wrap gap-2">
                {/* Spotify */}
                {user.social_links?.spotify && (
                  <a
                    href={user.social_links.spotify.url || '#'}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group bg-gradient-to-r from-green-600/20 to-green-700/20 hover:from-green-600/40 hover:to-green-700/40 border border-green-500/50 hover:border-green-400/70 rounded-lg px-3 py-1.5 flex items-center gap-2 transition-all shadow-lg hover:shadow-green-500/20 flex-shrink-1 min-w-[120px] max-w-[180px]"
                    style={{ flex: '0 1 auto' }}
                  >
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                      <svg className="w-4 h-4 text-green-400" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/>
                      </svg>
                    </div>
                    <div className="text-left overflow-hidden">
                      <div className="text-xs font-semibold text-green-300 group-hover:text-green-200 truncate">Spotify</div>
                    </div>
                  </a>
                )}

                {/* SoundCloud */}
                {user.social_links?.soundcloud && (
                  <a
                    href={user.social_links.soundcloud.url || '#'}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group bg-gradient-to-r from-orange-600/20 to-orange-700/20 hover:from-orange-600/40 hover:to-orange-700/40 border border-orange-500/50 hover:border-orange-400/70 rounded-lg px-3 py-1.5 flex items-center gap-2 transition-all shadow-lg hover:shadow-orange-500/20 flex-shrink-1 min-w-[120px] max-w-[180px]"
                    style={{ flex: '0 1 auto' }}
                  >
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                      <svg className="w-4 h-4 text-orange-400" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M1.175 12.225c-.051 0-.094.046-.101.1l-.233 2.154.233 2.105c.007.058.05.098.101.098.05 0 .09-.04.099-.098l.255-2.105-.27-2.154c0-.057-.045-.1-.09-.1m-.899.828c-.051 0-.09.04-.099.098L0 14.479l.176 1.428c0 .057.048.098.099.098.05 0 .089-.045.097-.098l.223-1.428-.223-1.328c-.008-.057-.047-.098-.097-.098m1.83-.567c-.05 0-.088.04-.096.097l-.199 1.896.199 1.848c.008.058.046.097.096.097.05 0 .091-.039.098-.097l.226-1.848-.226-1.896c-.007-.057-.048-.097-.098-.097m.93-.722c-.05 0-.09.04-.098.1l-.196 2.618.196 2.514c.008.06.049.098.098.098.05 0 .095-.04.098-.098l.226-2.514-.226-2.618c-.003-.058-.048-.1-.098-.1m.964-.91c-.053 0-.096.042-.104.1l-.18 3.528.18 3.453c.008.061.05.101.104.101.049 0 .095-.04.104-.101l.206-3.453-.206-3.528c-.009-.058-.055-.1-.104-.1m1.713-.517c-.058 0-.101.046-.109.1l-.165 4.045.165 3.938c.008.058.051.098.109.098.056 0 .104-.04.109-.098l.193-3.938-.193-4.045c-.005-.054-.053-.1-.109-.1m1.773-.573c-.061 0-.106.046-.115.098l-.16 4.618.16 4.483c.009.056.054.098.115.098.058 0 .104-.042.111-.098l.181-4.483-.181-4.618c-.007-.052-.053-.098-.111-.098m1.797-.605c-.061 0-.109.045-.115.1l-.15 5.223.15 5.076c.006.057.054.099.115.099.059 0 .108-.042.113-.099l.18-5.076-.18-5.223c-.005-.055-.054-.1-.113-.1m1.826-.652c-.062 0-.111.048-.114.099l-.145 5.875.145 5.735c.003.058.052.1.114.1.059 0 .111-.045.114-.1l.169-5.735-.169-5.875c-.003-.051-.055-.099-.114-.099m1.477.065c-.052 0-.094.04-.1.092l-.164 5.81.164 5.67c.006.054.048.095.1.095.051 0 .093-.041.1-.095l.19-5.67-.19-5.81c-.007-.051-.049-.092-.1-.092m1.848-.092c-.052 0-.091.04-.099.092l-.155 5.902.155 5.773c.008.051.047.092.099.092.058 0 .105-.041.109-.092l.174-5.773-.174-5.902c-.004-.051-.051-.092-.109-.092m1.817.029c-.054 0-.099.042-.109.096l-.145 5.873.145 5.744c.01.054.055.096.109.096.058 0 .104-.042.115-.096l.164-5.744-.164-5.873c-.011-.054-.057-.096-.115-.096m1.784-.092c-.058 0-.104.041-.111.096l-.142 5.965.142 5.814c.007.055.053.096.111.096.058 0 .104-.041.111-.096l.161-5.814-.161-5.965c-.007-.055-.053-.096-.111-.096"/>
                      </svg>
                    </div>
                    <div className="text-left overflow-hidden">
                      <div className="text-xs font-semibold text-orange-300 group-hover:text-orange-200 truncate">SoundCloud</div>
                    </div>
                  </a>
                )}

                {/* Instagram */}
                {user.social_links?.instagram && (
                  <a
                    href={user.social_links.instagram.url || '#'}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group bg-gradient-to-r from-pink-600/20 to-purple-700/20 hover:from-pink-600/40 hover:to-purple-700/40 border border-pink-500/50 hover:border-pink-400/70 rounded-lg px-3 py-1.5 flex items-center gap-2 transition-all shadow-lg hover:shadow-pink-500/20 flex-shrink-1 min-w-[120px] max-w-[180px]"
                    style={{ flex: '0 1 auto' }}
                  >
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                      <svg className="w-4 h-4 text-pink-400" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                      </svg>
                    </div>
                    <div className="text-left overflow-hidden">
                      <div className="text-xs font-semibold text-pink-300 group-hover:text-pink-200 truncate">Instagram</div>
                    </div>
                  </a>
                )}

                {/* YouTube */}
                {user.social_links?.youtube && (
                  <a
                    href={user.social_links.youtube.url || '#'}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group bg-gradient-to-r from-red-600/20 to-red-700/20 hover:from-red-600/40 hover:to-red-700/40 border border-red-500/50 hover:border-red-400/70 rounded-lg px-3 py-1.5 flex items-center gap-2 transition-all shadow-lg hover:shadow-red-500/20 flex-shrink-1 min-w-[120px] max-w-[180px]"
                    style={{ flex: '0 1 auto' }}
                  >
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                      <svg className="w-4 h-4 text-red-400" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                      </svg>
                    </div>
                    <div className="text-left overflow-hidden">
                      <div className="text-xs font-semibold text-red-300 group-hover:text-red-200 truncate">YouTube</div>
                    </div>
                  </a>
                )}
                
                {/* If no social links, show a message */}
                {(!user.social_links || Object.keys(user.social_links).length === 0) && (
                  <div className="text-gray-400 text-sm italic">No social links added yet</div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Profile Completion Banner */}
        {showCompletionBanner && (
          <div className="mb-6 bg-gradient-to-r from-cyan-600/20 via-purple-600/20 to-pink-600/20 border border-cyan-500/40 rounded-xl p-4 flex items-center justify-between backdrop-blur-sm animate-fade-in">
            <div className="flex items-center gap-3">
              <span className="text-2xl">✨</span>
              <div>
                <p className="text-white font-semibold">Complete your profile</p>
                <p className="text-gray-300 text-sm">Add a bio and showcase your vibe!</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowEditProfileModal(true)}
                className="bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white px-4 py-2 rounded-lg transition-all font-medium text-sm"
              >
                Complete Profile
              </button>
              <button
                onClick={() => {
                  setShowCompletionBanner(false);
                  localStorage.setItem(`profile_completion_banner_dismissed_${userId}`, 'true');
                }}
                className="text-gray-400 hover:text-white transition-colors p-2"
              >
                <X size={20} />
              </button>
            </div>
          </div>
        )}

        {/* Tab Navigation */}
        <div className="profile-tabs-wrapper relative">
          <div className="profile-tabs-row flex gap-3 mb-6 overflow-x-auto pb-2">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-6 py-3 rounded-xl font-medium transition-all whitespace-nowrap ${
                activeTab === tab.id
                  ? `bg-gradient-to-r ${tab.gradient} text-white shadow-lg scale-105`
                  : 'bg-slate-800/50 text-gray-400 hover:bg-slate-700/50'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
        </div>

        {/* Tab Content Container */}
        <div>
          {/* Tab Content - Overview */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
            {/* Bio Panel - About [Username] */}
            {user.bio && (
              <div className="bg-gradient-to-br from-indigo-800/90 via-purple-900/40 to-indigo-800/90 backdrop-blur-xl rounded-2xl p-6 border border-indigo-500/40 shadow-xl">
                <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                  <MessageCircle size={24} className="text-indigo-400" />
                  About {user.name}
                </h3>
                <p className="text-gray-200 text-base leading-relaxed whitespace-pre-wrap">
                  {user.bio}
                </p>
              </div>
            )}

            {/* My Favorites Section - Standalone Card (only on own profile) */}
            {currentUser && (currentUser.user_id === userId || currentUser.id === userId) && (
              <div className="bg-gradient-to-br from-slate-800/90 via-purple-900/30 to-slate-800/90 backdrop-blur-xl rounded-2xl p-6 border border-purple-500/30 shadow-xl">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-bold text-white flex items-center gap-2">
                    <Heart className="w-6 h-6 text-purple-400" fill="#a855f7" />
                    My Favorites
                  </h3>
                  <button
                    onClick={() => { setShowFavoritesModal(true); fetchFavorites(); }}
                    className="px-4 py-2 bg-purple-600 hover:bg-purple-500 text-white rounded-lg text-sm font-medium transition-colors"
                  >
                    View All →
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-slate-700/40 rounded-lg p-4 border border-cyan-500/20">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-2xl">🎵</span>
                      <span className="text-gray-300 text-sm">Music</span>
                    </div>
                    <div className="text-2xl font-bold text-cyan-400">{user.favorite_count || 0}</div>
                    <p className="text-xs text-gray-500">Favorite tracks</p>
                  </div>
                  <div className="bg-slate-700/40 rounded-lg p-4 border border-red-500/20">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-2xl">🏢</span>
                      <span className="text-gray-300 text-sm">Venues</span>
                    </div>
                    <div className="text-2xl font-bold text-red-400">{savedVenues.length}</div>
                    <p className="text-xs text-gray-500">Saved venues</p>
                  </div>
                </div>
              </div>
            )}

            {/* Mutual Friends Section */}
            {profileData.mutual_friends && profileData.mutual_friends.length > 0 && (
              <div className="bg-gradient-to-br from-slate-800/90 via-purple-900/30 to-slate-800/90 backdrop-blur-xl rounded-2xl p-6 border border-purple-500/30 shadow-xl">
                <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                  <Users size={24} className="text-purple-400" />
                  {stats.mutual_friends_count} Mutual {stats.mutual_friends_count === 1 ? 'Friend' : 'Friends'}
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
                  {profileData.mutual_friends.slice(0, 10).map((friend) => (
                    <div
                      key={friend.id}
                      onClick={() => navigate(`/profile/${friend.id}`)}
                      className="flex flex-col items-center cursor-pointer group"
                    >
                      <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-600 to-pink-600 flex items-center justify-center text-white font-bold text-xl overflow-hidden border-2 border-purple-500/50 group-hover:border-purple-400 transition-all group-hover:scale-110">
                        {friend.profile_picture ? (
                          <img src={`${API}${friend.profile_picture.replace('/uploads/', '/api/uploads/')}`} alt={friend.name} className="w-full h-full object-cover" />
                        ) : (
                          friend.name?.charAt(0).toUpperCase() || '?'
                        )}
                      </div>
                      <span className="text-xs text-gray-300 mt-2 text-center group-hover:text-purple-300 transition-colors">{friend.name}</span>
                    </div>
                  ))}
                </div>
                {stats.mutual_friends_count > 10 && (
                  <p
                    onClick={() => setShowMutualModal(true)}
                    data-testid="view-all-mutuals-link"
                    className="text-sm text-cyan-400 mt-4 text-center cursor-pointer hover:text-cyan-300 hover:underline"
                  >
                    + {stats.mutual_friends_count - 10} more mutual friends — View all
                  </p>
                )}
              </div>
            )}

            {/* My Vibe - MOVED ABOVE RECENT ACTIVITY */}
            {music.top_genres && music.top_genres.length > 0 && (
              <div className="bg-gradient-to-br from-slate-800/90 via-pink-900/30 to-slate-800/90 backdrop-blur-xl rounded-2xl p-6 border border-pink-500/30 shadow-xl">
                <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                  <svg className="w-6 h-6 text-pink-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                  </svg>
                  My Vibe
                </h3>
                <div className="flex flex-wrap gap-3">
                  {music.top_genres.slice(0, 3).map((genre, index) => (
                    <span
                      key={index}
                      className="bg-gradient-to-r from-pink-600/30 to-purple-600/30 border border-pink-500/50 text-pink-200 px-4 py-2 rounded-full text-sm font-semibold"
                    >
                      {genre}
                    </span>
                  ))}
                  {music.spotify?.connected && (
                    <span className="bg-gradient-to-r from-green-600/30 to-emerald-600/30 border border-green-500/50 text-green-200 px-4 py-2 rounded-full text-sm font-semibold">
                      ✓ Active Listener
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* ID Hunter Record — engine scans vs. clips ID'd for other BeatSeekers */}
            <HunterRecordCard userId={user.id} />

            {/* Recent Activity Summary */}
            <div className="bg-gradient-to-br from-slate-800/90 via-cyan-900/30 to-slate-800/90 backdrop-blur-xl rounded-2xl p-6 border border-cyan-500/30 shadow-xl">
              <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                <svg className="w-6 h-6 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                Recent Activity
                <span className="text-sm text-gray-400 font-normal ml-2">(Last 7 days)</span>
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {/* Tracks Identified */}
                <div className="bg-slate-700/30 rounded-xl p-4 border border-cyan-500/20">
                  <div className="text-3xl font-bold text-cyan-400">{recent_activity.tracks_identified_7d || 0}</div>
                  <div className="text-sm text-gray-400 mt-1 flex items-center justify-between">
                    <span>Tracks Identified</span>
                    <button
                      onClick={() => alert('Coming soon!')}
                      className="text-xs text-cyan-400 hover:text-cyan-300 transition-colors ml-2"
                    >
                      View →
                    </button>
                  </div>
                </div>
                
                {/* Shared Tracks */}
                <div className="bg-slate-700/30 rounded-xl p-4 border border-blue-500/20">
                  <div className="text-3xl font-bold text-blue-400">{recent_activity.shared_tracks_7d || 0}</div>
                  <div className="text-sm text-gray-400 mt-1 flex items-center justify-between">
                    <span>Shared Tracks</span>
                    <button
                      onClick={() => alert('Coming soon!')}
                      className="text-xs text-blue-400 hover:text-blue-300 transition-colors ml-2"
                    >
                      View →
                    </button>
                  </div>
                </div>
                
                {/* Messages in Chat */}
                <div className="bg-slate-700/30 rounded-xl p-4 border border-purple-500/20">
                  <div className="text-3xl font-bold text-purple-400">{recent_activity.chat_messages_7d}</div>
                  <div className="text-sm text-gray-400 mt-1 flex items-center justify-between">
                    <span>Messages in Chat</span>
                    <button
                      onClick={() => alert('Coming soon!')}
                      className="text-xs text-purple-400 hover:text-purple-300 transition-colors ml-2"
                    >
                      View →
                    </button>
                  </div>
                </div>
                
                {/* Forum Posts */}
                <div className="bg-slate-700/30 rounded-xl p-4 border border-pink-500/20">
                  <div className="text-3xl font-bold text-pink-400">{recent_activity.forum_posts_7d}</div>
                  <div className="text-sm text-gray-400 mt-1 flex items-center justify-between">
                    <span>Forum Posts</span>
                    <button
                      onClick={() => alert('Coming soon!')}
                      className="text-xs text-pink-400 hover:text-pink-300 transition-colors ml-2"
                    >
                      View →
                    </button>
                  </div>
                </div>
                
                {/* Events Attended */}
                <div className="bg-slate-700/30 rounded-xl p-4 border border-green-500/20">
                  <div className="text-3xl font-bold text-green-400">0</div>
                  <div className="text-sm text-gray-400 mt-1 flex items-center justify-between">
                    <span>Events Attended</span>
                    <button
                      onClick={() => alert('Coming soon!')}
                      className="text-xs text-green-400 hover:text-green-300 transition-colors ml-2"
                    >
                      View →
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Stats Cards */}
            <div className="bg-gradient-to-br from-slate-800/90 via-blue-900/30 to-slate-800/90 backdrop-blur-xl rounded-2xl p-6 border border-blue-500/30 shadow-xl">
              <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                <svg className="w-6 h-6 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                All-Time Stats
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-slate-700/30 rounded-xl p-4 border border-blue-500/20 text-center">
                  <div className="text-2xl font-bold text-blue-400">{user.total_identifications || 0}</div>
                  <div className="text-xs text-gray-400 mt-1">Track IDs</div>
                </div>
                <div className="bg-slate-700/30 rounded-xl p-4 border border-purple-500/20 text-center">
                  <div className="text-2xl font-bold text-purple-400">{user.favorite_count || 0}</div>
                  <div className="text-xs text-gray-400 mt-1">Favorites</div>
                </div>
                <div className="bg-slate-700/30 rounded-xl p-4 border border-green-500/20 text-center">
                  <div className="text-2xl font-bold text-green-400">{stats.followers_count}</div>
                  <div className="text-xs text-gray-400 mt-1">Followers</div>
                </div>
                <div className="bg-slate-700/30 rounded-xl p-4 border border-orange-500/20 text-center">
                  <div className="text-2xl font-bold text-orange-400">{stats.profile_views}</div>
                  <div className="text-xs text-gray-400 mt-1">Profile Views</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tab Content - Music */}
        {activeTab === 'music' && (
          <div className="space-y-6">

            {/* Monthly Top 10 */}
            {!loadingTop10 && monthlyTop10.tracks && monthlyTop10.tracks.length > 0 && (
              <div className="bg-gradient-to-br from-slate-800/90 via-purple-900/40 to-pink-900/30 backdrop-blur-xl rounded-xl p-4 border border-purple-500/40 shadow-xl">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold text-white flex items-center gap-2">
                    <svg className="w-5 h-5 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                    </svg>
                    {monthlyTop10.month}&apos;s Top 10
                  </h3>
                  
                  {/* Navigation arrows */}
                  {monthlyTop10.tracks.length > 5 && (
                    <div className="flex gap-2">
                      <button
                        onClick={() => setMonthlyPage(0)}
                        disabled={monthlyPage === 0}
                        className="p-1.5 rounded-lg bg-purple-600/20 hover:bg-purple-600/40 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                      >
                        <svg className="w-4 h-4 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                      </button>
                      <button
                        onClick={() => setMonthlyPage(1)}
                        disabled={monthlyPage === 1 || monthlyTop10.tracks.length <= 5}
                        className="p-1.5 rounded-lg bg-purple-600/20 hover:bg-purple-600/40 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                      >
                        <svg className="w-4 h-4 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    </div>
                  )}
                </div>
                
                <div className="space-y-2">
                  {monthlyTop10.tracks.slice(monthlyPage * 5, monthlyPage * 5 + 5).map((track, index) => {
                    const actualIndex = monthlyPage * 5 + index;
                    return (
                      <div 
                        key={actualIndex}
                        className="bg-slate-700/30 rounded-lg p-3 border border-purple-500/30 hover:border-purple-400/50 transition-all group"
                      >
                        <div className="flex items-center gap-3">
                          {/* Rank */}
                          <div className="text-xl font-bold text-purple-400 w-8 text-center flex-shrink-0">
                            #{actualIndex + 1}
                          </div>
                          
                          {/* Album Art or Music Icon */}
                          <div className="w-12 h-12 bg-slate-800 rounded-lg flex items-center justify-center overflow-hidden flex-shrink-0">
                            {track.album_art ? (
                              <img src={track.album_art} alt={track.track_name} className="w-full h-full object-cover" />
                            ) : (
                              <svg className="w-6 h-6 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                              </svg>
                            )}
                          </div>
                          
                          {/* Track Info */}
                          <div className="flex-1 min-w-0">
                            <div className="text-white font-semibold truncate text-sm">{track.track_name}</div>
                            <div className="text-gray-400 text-xs truncate">{track.artist}</div>
                            
                            {/* Stats */}
                            <div className="flex items-center gap-2 mt-1 text-xs text-gray-400">
                              {track.stats.ids > 0 && (
                                <span className="flex items-center gap-0.5">
                                  <span>👆</span> {track.stats.ids}
                                </span>
                              )}
                              {track.stats.shares > 0 && (
                                <span className="flex items-center gap-0.5">
                                  <span>💫</span> {track.stats.shares}
                                </span>
                              )}
                              {track.stats.reshares > 0 && (
                                <span className="flex items-center gap-0.5">
                                  <span>🔄</span> {track.stats.reshares}
                                </span>
                              )}
                              {track.stats.favorited && (
                                <span className="flex items-center gap-0.5">
                                  <span>⭐</span>
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* My Spotify Tracks */}
            <div className="bg-gradient-to-br from-green-900/40 via-green-800/20 to-slate-800/90 backdrop-blur-xl rounded-2xl p-6 border border-green-500/50 shadow-xl">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-white flex items-center gap-2">
                  <svg className="w-6 h-6 text-green-400" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/>
                  </svg>
                  My Spotify Tracks
                </h3>
                {music.spotify?.connected && music.spotify.top_tracks && music.spotify.top_tracks.length > 0 && (
                  <div className="flex gap-2">
                    <button
                      onClick={() => setTopTracksView('month')}
                      className={`px-3 py-1 rounded-lg text-sm font-medium transition-all ${
                        topTracksView === 'month'
                          ? 'bg-green-600 text-white'
                          : 'bg-slate-700/50 text-gray-400 hover:bg-slate-600/50'
                      }`}
                    >
                      This Month
                    </button>
                    <button
                      onClick={() => setTopTracksView('alltime')}
                      className={`px-3 py-1 rounded-lg text-sm font-medium transition-all ${
                        topTracksView === 'alltime'
                          ? 'bg-green-600 text-white'
                          : 'bg-slate-700/50 text-gray-400 hover:bg-slate-600/50'
                      }`}
                    >
                      All Time
                    </button>
                  </div>
                )}
              </div>
              
              {music.spotify?.connected && music.spotify.top_tracks && music.spotify.top_tracks.length > 0 ? (
                <div className="space-y-3">
                  {music.spotify.top_tracks.map((track, index) => (
                    <div
                      key={index}
                      onClick={() => track.spotify_url && window.open(track.spotify_url, '_blank')}
                      className="flex items-center gap-3 bg-green-900/20 rounded-lg p-3 border border-green-500/30 hover:border-green-400/50 hover:bg-green-900/30 transition-all cursor-pointer group"
                    >
                      <div className="w-8 h-8 bg-green-600 rounded flex items-center justify-center text-white font-bold text-sm">
                        {index + 1}
                      </div>
                      <div className="flex-1">
                        <div className="text-white font-semibold group-hover:text-green-300 transition-colors">{track.name}</div>
                        <div className="text-xs text-gray-400">{track.artist}</div>
                      </div>
                      <svg className="w-5 h-5 text-green-400 group-hover:scale-110 transition-transform" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/>
                      </svg>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="bg-slate-700/30 rounded-lg p-8 border border-green-500/20 text-center">
                  <svg className="w-12 h-12 text-gray-500 mx-auto mb-3" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/>
                  </svg>
                  <p className="text-gray-400 text-sm italic">Spotify not connected</p>
                </div>
              )}
            </div>

            {/* Favorite Playlists */}
            {music.spotify?.connected && music.spotify.top_playlists && music.spotify.top_playlists.length > 0 && (
              <div className="bg-gradient-to-br from-slate-800/90 via-purple-900/30 to-slate-800/90 backdrop-blur-xl rounded-2xl p-6 border border-purple-500/30 shadow-xl">
                <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                  <svg className="w-6 h-6 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                  </svg>
                  Favorite Playlists
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {music.spotify.top_playlists.map((playlist, index) => (
                    <div
                      key={index}
                      className="bg-slate-700/30 rounded-xl p-4 border border-purple-500/20 hover:border-purple-500/40 transition-all group cursor-pointer"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-16 h-16 bg-purple-600/30 rounded-lg flex items-center justify-center">
                          <svg className="w-8 h-8 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                          </svg>
                        </div>
                        <div className="flex-1">
                          <div className="text-white font-semibold group-hover:text-purple-300 transition-colors">
                            {playlist.name || `Playlist ${index + 1}`}
                          </div>
                          <div className="text-xs text-gray-400">{playlist.tracks || 0} tracks</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* My BEATSEEK Tracks */}
            <div className="bg-gradient-to-br from-slate-800/90 via-blue-900/30 to-slate-800/90 backdrop-blur-xl rounded-2xl p-6 border border-blue-500/30 shadow-xl">
              <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                <svg className="w-6 h-6 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                </svg>
                My BEATSEEK Tracks
              </h3>
              <div className="bg-slate-700/30 rounded-lg p-8 border border-blue-500/20 text-center">
                <p className="text-gray-400 text-sm italic">No tracks uploaded or shared yet</p>
              </div>
            </div>

            {/* My BEATSEEK Music Journey Section */}
            <div className="bg-gradient-to-br from-slate-800/90 via-cyan-900/30 to-slate-800/90 backdrop-blur-xl rounded-2xl p-6 border border-cyan-500/30 shadow-xl">
              <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                <svg className="w-6 h-6 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
                My BEATSEEK Music Journey
              </h3>
              
              <div className="space-y-4">
                {/* Track IDs */}
                <div className="bg-slate-700/30 rounded-lg p-4 border border-cyan-500/20">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <svg className="w-5 h-5 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span className="text-white font-semibold">Track IDs</span>
                      <button
                        onClick={() => alert('Coming soon!')}
                        className="text-xs text-cyan-400 hover:text-cyan-300 transition-colors ml-2"
                      >
                        View →
                      </button>
                    </div>
                    <span className="text-cyan-400 font-bold">{user.total_identifications || 0}</span>
                  </div>
                  <p className="text-xs text-gray-400">Total tracks identified using BEATSEEK</p>
                </div>

                {/* Shared Tracks */}
                <div className="bg-slate-700/30 rounded-lg p-4 border border-blue-500/20">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <svg className="w-5 h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                      </svg>
                      <span className="text-white font-semibold">Shared Tracks</span>
                      <button
                        onClick={() => alert('Coming soon!')}
                        className="text-xs text-blue-400 hover:text-blue-300 transition-colors ml-2"
                      >
                        View →
                      </button>
                    </div>
                    <span className="text-blue-400 font-bold">0</span>
                  </div>
                  <p className="text-xs text-gray-400">Tracks shared with the community</p>
                </div>

                {/* Favorited Tracks */}
                <div className="bg-slate-700/30 rounded-lg p-4 border border-purple-500/20">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <svg className="w-5 h-5 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                      </svg>
                      <span className="text-white font-semibold">My Favorites</span>
                      <button
                        onClick={() => { setShowFavoritesModal(true); fetchFavorites(); }}
                        className="text-xs text-purple-400 hover:text-purple-300 transition-colors ml-2"
                      >
                        View →
                      </button>
                    </div>
                    <span className="text-purple-400 font-bold">{(user.favorite_count || 0) + savedVenues.length}</span>
                  </div>
                  <p className="text-xs text-gray-400">Music &amp; venues you&apos;ve saved</p>
                </div>
              </div>
            </div>


            {/* All-Time Top 10 */}
            {!loadingTop10 && alltimeTop10.tracks && alltimeTop10.tracks.length > 0 && (
              <div className="bg-gradient-to-br from-slate-800/90 via-yellow-900/30 to-slate-800/90 backdrop-blur-xl rounded-xl p-4 border border-yellow-500/30 shadow-xl">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold text-white flex items-center gap-2">
                    <svg className="w-5 h-5 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                    </svg>
                    My All-Time Top 10
                  </h3>
                  
                  {/* Navigation arrows */}
                  {alltimeTop10.tracks.length > 5 && (
                    <div className="flex gap-2">
                      <button
                        onClick={() => setAlltimePage(0)}
                        disabled={alltimePage === 0}
                        className="p-1.5 rounded-lg bg-yellow-600/20 hover:bg-yellow-600/40 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                      >
                        <svg className="w-4 h-4 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                      </button>
                      <button
                        onClick={() => setAlltimePage(1)}
                        disabled={alltimePage === 1 || alltimeTop10.tracks.length <= 5}
                        className="p-1.5 rounded-lg bg-yellow-600/20 hover:bg-yellow-600/40 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                      >
                        <svg className="w-4 h-4 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    </div>
                  )}
                </div>
                
                <div className="space-y-2">
                  {alltimeTop10.tracks.slice(alltimePage * 5, alltimePage * 5 + 5).map((track, index) => {
                    const actualIndex = alltimePage * 5 + index;
                    return (
                      <div 
                        key={actualIndex}
                        className="bg-slate-700/30 rounded-lg p-3 border border-yellow-500/20 hover:border-yellow-400/40 transition-all group"
                      >
                        <div className="flex items-center gap-3">
                          {/* Rank */}
                          <div className="text-xl font-bold text-yellow-400 w-8 text-center flex-shrink-0">
                            #{actualIndex + 1}
                          </div>
                          
                          {/* Album Art or Music Icon */}
                          <div className="w-12 h-12 bg-slate-800 rounded-lg flex items-center justify-center overflow-hidden flex-shrink-0">
                            {track.album_art ? (
                              <img src={track.album_art} alt={track.track_name} className="w-full h-full object-cover" />
                            ) : (
                              <svg className="w-6 h-6 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                              </svg>
                            )}
                          </div>
                          
                          {/* Track Info */}
                          <div className="flex-1 min-w-0">
                            <div className="text-white font-semibold truncate text-sm">{track.track_name}</div>
                            <div className="text-gray-400 text-xs truncate">{track.artist}</div>
                            
                            {/* Stats */}
                            <div className="flex items-center gap-2 mt-1 text-xs text-gray-400">
                              {track.stats.ids > 0 && (
                                <span className="flex items-center gap-0.5">
                                  <span>👆</span> {track.stats.ids}
                                </span>
                              )}
                              {track.stats.shares > 0 && (
                                <span className="flex items-center gap-0.5">
                                  <span>💫</span> {track.stats.shares}
                                </span>
                              )}
                              {track.stats.reshares > 0 && (
                                <span className="flex items-center gap-0.5">
                                  <span>🔄</span> {track.stats.reshares}
                                </span>
                              )}
                              {track.stats.favorited && (
                                <span className="flex items-center gap-0.5">
                                  <span>⭐</span>
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

          </div>
        )}

        {/* Tab Content - Activity */}
        {activeTab === 'activity' && (
          <div className="space-y-6">
            {loadingActivity ? (
              <div className="bg-slate-800/50 backdrop-blur rounded-xl p-8 border border-green-500/20 text-center">
                <div className="text-white text-lg animate-pulse">Loading activity...</div>
              </div>
            ) : activityData.length === 0 ? (
              <div className="bg-slate-800/50 backdrop-blur rounded-xl p-8 border border-green-500/20 text-center">
                <div className="text-6xl mb-4">📭</div>
                <h3 className="text-2xl font-bold text-white mb-2">No Activity Yet</h3>
                <p className="text-gray-400">Start identifying tracks, sharing music, and engaging with the community!</p>
              </div>
            ) : (
              <div className="bg-gradient-to-br from-slate-800/90 via-green-900/20 to-slate-800/90 backdrop-blur-xl rounded-2xl p-6 border border-green-500/30 shadow-xl">
                <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
                  <svg className="w-6 h-6 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  My BEATSEEK Social Journey
                  <span className="text-sm text-gray-400 font-normal ml-2">({activityData.length} items)</span>
                </h3>
                
                <div className="space-y-3 max-h-[600px] overflow-y-auto pr-2">
                  {activityData.map((activity, index) => (
                    <div
                      key={index}
                      className="bg-slate-700/30 rounded-lg p-4 border border-green-500/20 hover:border-green-400/40 transition-all group"
                    >
                      <div className="flex items-start gap-3">
                        {/* Icon */}
                        <div className="flex-shrink-0">
                          {activity.type === 'track_identified' && (
                            <div className="w-10 h-10 bg-cyan-600 rounded-full flex items-center justify-center">
                              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                              </svg>
                            </div>
                          )}
                          {activity.type === 'track_shared' && (
                            <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center">
                              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                              </svg>
                            </div>
                          )}
                          {activity.type === 'chat_message' && (
                            <div className="w-10 h-10 bg-purple-600 rounded-full flex items-center justify-center">
                              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                              </svg>
                            </div>
                          )}
                          {activity.type === 'forum_post' && (
                            <div className="w-10 h-10 bg-pink-600 rounded-full flex items-center justify-center">
                              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                            </div>
                          )}
                        </div>
                        
                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              {activity.type === 'track_identified' && (
                                <>
                                  <div className="flex items-center gap-2 mb-1">
                                    <p className="text-white font-medium group-hover:text-cyan-300 transition-colors">
                                      Identified: <span className="font-semibold">{activity.data.track_name}</span>
                                    </p>
                                    {isWithin7Days(activity.timestamp) && (
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleGoToActivity(activity);
                                        }}
                                        className="text-xs text-cyan-400 hover:text-cyan-300 font-medium transition-colors"
                                      >
                                        Go to →
                                      </button>
                                    )}
                                  </div>
                                  <p className="text-sm text-gray-400 mt-1">by {activity.data.artist}</p>
                                </>
                              )}
                              {activity.type === 'track_shared' && (
                                <>
                                  <div className="flex items-center gap-2 mb-1">
                                    <p className="text-white font-medium group-hover:text-blue-300 transition-colors">
                                      Shared: <span className="font-semibold">{activity.data.track_name}</span>
                                    </p>
                                    {isWithin7Days(activity.timestamp) && (
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleGoToActivity(activity);
                                        }}
                                        className="text-xs text-blue-400 hover:text-blue-300 font-medium transition-colors"
                                      >
                                        Go to →
                                      </button>
                                    )}
                                  </div>
                                  <p className="text-sm text-gray-400 mt-1">
                                    by {activity.data.artist}
                                    {activity.data.message && ` • "${activity.data.message}"`}
                                  </p>
                                </>
                              )}
                              {activity.type === 'chat_message' && (
                                <>
                                  <div className="flex items-center gap-2 mb-1">
                                    <p className="text-white font-medium group-hover:text-purple-300 transition-colors">
                                      Posted in <span className="font-semibold">#{activity.data.room}</span> chat
                                    </p>
                                    {isWithin7Days(activity.timestamp) && (
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleGoToActivity(activity);
                                        }}
                                        className="text-xs text-purple-400 hover:text-purple-300 font-medium transition-colors"
                                      >
                                        Go to →
                                      </button>
                                    )}
                                  </div>
                                  <p className="text-sm text-gray-400 mt-1 italic">&quot;{activity.data.message_preview}&quot;</p>
                                </>
                              )}
                              {activity.type === 'forum_post' && (
                                <>
                                  <div className="flex items-center gap-2 mb-1">
                                    <p className="text-white font-medium group-hover:text-pink-300 transition-colors">
                                      Created post: <span className="font-semibold">{activity.data.title}</span>
                                    </p>
                                    {isWithin7Days(activity.timestamp) && (
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleGoToActivity(activity);
                                        }}
                                        className="text-xs text-pink-400 hover:text-pink-300 font-medium transition-colors"
                                      >
                                        Go to →
                                      </button>
                                    )}
                                  </div>
                                  <p className="text-sm text-gray-400 mt-1">
                                    in {activity.data.category} forum
                                  </p>
                                </>
                              )}
                            </div>
                            <span className="text-xs text-gray-500 whitespace-nowrap">
                              {formatTimestamp(activity.timestamp)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Tab Content - Community */}
        {activeTab === 'community' && (
          <div className="space-y-6">
            {loadingCommunity ? (
              <div className="bg-slate-800/50 backdrop-blur rounded-xl p-8 border border-orange-500/20 text-center">
                <div className="text-white text-lg animate-pulse">Loading community...</div>
              </div>
            ) : !communityData ? (
              <div className="bg-slate-800/50 backdrop-blur rounded-xl p-8 border border-orange-500/20 text-center">
                <p className="text-gray-400">Failed to load community data</p>
              </div>
            ) : (
              <>
                {/* Friends Section */}
                <div className="bg-gradient-to-br from-slate-800/90 via-green-900/20 to-slate-800/90 backdrop-blur-xl rounded-2xl p-6 border border-green-500/30 shadow-xl">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-bold text-white flex items-center gap-2">
                      <Users size={24} className="text-green-400" />
                      Friends
                      <span className="text-sm text-gray-400 font-normal ml-2">({communityData.counts.friends})</span>
                    </h3>
                    {communityData.friends.length > 5 && (
                      <button
                        onClick={() => openViewAllModal('Friends', communityData.friends)}
                        className="text-sm text-green-400 hover:text-green-300 transition-colors font-medium"
                      >
                        View All →
                      </button>
                    )}
                  </div>
                  
                  {communityData.friends.length === 0 ? (
                    <div className="bg-slate-700/30 rounded-lg p-8 border border-green-500/20 text-center">
                      <p className="text-gray-400 text-sm italic">No friends yet</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                      {communityData.friends.slice(0, 5).map((friend) => (
                        <div
                          key={friend.id}
                          className="bg-slate-700/30 rounded-lg p-3 border border-green-500/20 hover:border-green-400/40 transition-all group relative"
                        >
                          {/* Avatar with Recently Active Indicator */}
                          <div
                            className="relative mb-2 cursor-pointer"
                            onClick={() => navigate(`/profile/${friend.id}`)}
                          >
                            <div className="w-16 h-16 mx-auto rounded-full bg-gradient-to-br from-green-600 to-emerald-600 flex items-center justify-center text-white font-bold text-xl overflow-hidden border-2 border-green-500/50 group-hover:border-green-400 transition-all">
                              {friend.profile_picture ? (
                                <img src={`${API}${friend.profile_picture.replace('/uploads/', '/api/uploads/')}`} alt={friend.name} className="w-full h-full object-cover" />
                              ) : (
                                friend.name?.charAt(0).toUpperCase() || '?'
                              )}
                            </div>
                            {isRecentlyActive(friend.last_seen) && (
                              <div className="absolute bottom-0 right-1/4 w-4 h-4 bg-green-500 border-2 border-slate-800 rounded-full"></div>
                            )}
                          </div>
                          
                          {/* Name */}
                          <p
                            className="text-sm text-white font-medium text-center truncate cursor-pointer group-hover:text-green-300 transition-colors"
                            onClick={() => navigate(`/profile/${friend.id}`)}
                          >
                            {friend.name}
                          </p>
                          
                          {/* Last Seen */}
                          <p className="text-xs text-gray-500 text-center mt-1">
                            {formatLastSeen(friend.last_seen)}
                          </p>
                          
                          {/* Action Button - Only show for viewer who is logged in and not viewing own profile */}
                          {currentUser && (currentUser.user_id !== friend.id && currentUser.id !== friend.id) && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                if (friend.viewer_is_following) {
                                  handleUnfollowUser(friend.id);
                                } else {
                                  handleFollowUser(friend.id);
                                }
                              }}
                              className={`mt-2 w-full px-2 py-1 rounded text-xs font-medium transition-all ${
                                friend.viewer_is_following
                                  ? 'bg-slate-600 hover:bg-slate-500 text-white'
                                  : 'bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 text-white'
                              }`}
                            >
                              {friend.viewer_is_following ? 'Unfollow' : 'Follow'}
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Followers Section */}
                <div className="bg-gradient-to-br from-slate-800/90 via-blue-900/20 to-slate-800/90 backdrop-blur-xl rounded-2xl p-6 border border-blue-500/30 shadow-xl">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-bold text-white flex items-center gap-2">
                      <svg className="w-6 h-6 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                      Followers
                      <span className="text-sm text-gray-400 font-normal ml-2">({communityData.counts.followers})</span>
                    </h3>
                    {communityData.followers.length > 5 && (
                      <button
                        onClick={() => openViewAllModal('Followers', communityData.followers)}
                        className="text-sm text-blue-400 hover:text-blue-300 transition-colors font-medium"
                      >
                        View All →
                      </button>
                    )}
                  </div>
                  
                  {communityData.followers.length === 0 ? (
                    <div className="bg-slate-700/30 rounded-lg p-8 border border-blue-500/20 text-center">
                      <p className="text-gray-400 text-sm italic">No followers yet</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                      {communityData.followers.slice(0, 5).map((follower) => (
                        <div
                          key={follower.id}
                          className="bg-slate-700/30 rounded-lg p-3 border border-blue-500/20 hover:border-blue-400/40 transition-all group relative"
                        >
                          <div
                            className="relative mb-2 cursor-pointer"
                            onClick={() => navigate(`/profile/${follower.id}`)}
                          >
                            <div className="w-16 h-16 mx-auto rounded-full bg-gradient-to-br from-blue-600 to-cyan-600 flex items-center justify-center text-white font-bold text-xl overflow-hidden border-2 border-blue-500/50 group-hover:border-blue-400 transition-all">
                              {follower.profile_picture ? (
                                <img src={`${API}${follower.profile_picture.replace('/uploads/', '/api/uploads/')}`} alt={follower.name} className="w-full h-full object-cover" />
                              ) : (
                                follower.name?.charAt(0).toUpperCase() || '?'
                              )}
                            </div>
                            {isRecentlyActive(follower.last_seen) && (
                              <div className="absolute bottom-0 right-1/4 w-4 h-4 bg-green-500 border-2 border-slate-800 rounded-full"></div>
                            )}
                          </div>
                          
                          <p
                            className="text-sm text-white font-medium text-center truncate cursor-pointer group-hover:text-blue-300 transition-colors"
                            onClick={() => navigate(`/profile/${follower.id}`)}
                          >
                            {follower.name}
                          </p>
                          
                          <p className="text-xs text-gray-500 text-center mt-1">
                            {formatLastSeen(follower.last_seen)}
                          </p>
                          
                          {currentUser && (currentUser.user_id !== follower.id && currentUser.id !== follower.id) && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                if (follower.viewer_is_following) {
                                  handleUnfollowUser(follower.id);
                                } else {
                                  handleFollowUser(follower.id);
                                }
                              }}
                              className={`mt-2 w-full px-2 py-1 rounded text-xs font-medium transition-all ${
                                follower.viewer_is_following
                                  ? 'bg-slate-600 hover:bg-slate-500 text-white'
                                  : 'bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 text-white'
                              }`}
                            >
                              {follower.viewer_is_following ? 'Unfollow' : 'Follow'}
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Following Section - Regular Users Only */}
                <div className="bg-gradient-to-br from-slate-800/90 via-purple-900/20 to-slate-800/90 backdrop-blur-xl rounded-2xl p-6 border border-purple-500/30 shadow-xl">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-bold text-white flex items-center gap-2">
                      <svg className="w-6 h-6 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                      </svg>
                      Following
                      <span className="text-sm text-gray-400 font-normal ml-2">
                        ({communityData.following.filter(f => !['dj', 'producer', 'streamer'].includes(f.user_type?.toLowerCase())).length})
                      </span>
                    </h3>
                    {communityData.following.filter(f => !['dj', 'producer', 'streamer'].includes(f.user_type?.toLowerCase())).length > 5 && (
                      <button
                        onClick={() => openViewAllModal('Following', communityData.following.filter(f => !['dj', 'producer', 'streamer'].includes(f.user_type?.toLowerCase())))}
                        className="text-sm text-purple-400 hover:text-purple-300 transition-colors font-medium"
                      >
                        View All →
                      </button>
                    )}
                  </div>
                  
                  {communityData.following.filter(f => !['dj', 'producer', 'streamer'].includes(f.user_type?.toLowerCase())).length === 0 ? (
                    <div className="bg-slate-700/30 rounded-lg p-8 border border-purple-500/20 text-center">
                      <p className="text-gray-400 text-sm italic">Not following any regular users yet</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                      {communityData.following.filter(f => !['dj', 'producer', 'streamer'].includes(f.user_type?.toLowerCase())).slice(0, 5).map((following) => (
                        <div
                          key={following.id}
                          className="bg-slate-700/30 rounded-lg p-3 border border-purple-500/20 hover:border-purple-400/40 transition-all group relative"
                        >
                          <div
                            className="relative mb-2 cursor-pointer"
                            onClick={() => navigate(`/profile/${following.id}`)}
                          >
                            <div className="w-16 h-16 mx-auto rounded-full bg-gradient-to-br from-purple-600 to-pink-600 flex items-center justify-center text-white font-bold text-xl overflow-hidden border-2 border-purple-500/50 group-hover:border-purple-400 transition-all">
                              {following.profile_picture ? (
                                <img src={`${API}${following.profile_picture.replace('/uploads/', '/api/uploads/')}`} alt={following.name} className="w-full h-full object-cover" />
                              ) : (
                                following.name?.charAt(0).toUpperCase() || '?'
                              )}
                            </div>
                            {isRecentlyActive(following.last_seen) && (
                              <div className="absolute bottom-0 right-1/4 w-4 h-4 bg-green-500 border-2 border-slate-800 rounded-full"></div>
                            )}
                          </div>
                          
                          <p
                            className="text-sm text-white font-medium text-center truncate cursor-pointer group-hover:text-purple-300 transition-colors"
                            onClick={() => navigate(`/profile/${following.id}`)}
                          >
                            {following.name}
                          </p>
                          
                          <p className="text-xs text-gray-500 text-center mt-1">
                            {formatLastSeen(following.last_seen)}
                          </p>
                          
                          {currentUser && (currentUser.user_id !== following.id && currentUser.id !== following.id) && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                if (following.viewer_is_following) {
                                  handleUnfollowUser(following.id);
                                } else {
                                  handleFollowUser(following.id);
                                }
                              }}
                              className={`mt-2 w-full px-2 py-1 rounded text-xs font-medium transition-all ${
                                following.viewer_is_following
                                  ? 'bg-slate-600 hover:bg-slate-500 text-white'
                                  : 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white'
                              }`}
                            >
                              {following.viewer_is_following ? 'Unfollow' : 'Follow'}
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {/* Tab Content - My Gear */}
        {activeTab === 'gear' && (
          <div className="space-y-6" data-testid="gear-tab-content">
            {loadingGear ? (
              <div className="bg-slate-800/50 backdrop-blur rounded-xl p-8 border border-amber-500/20 text-center">
                <div className="text-white text-lg animate-pulse">Loading gear collection...</div>
              </div>
            ) : (
              <>
                {/* Gear Header - Compact */}
                <div className="bg-gradient-to-br from-slate-800/90 via-amber-900/20 to-slate-800/90 backdrop-blur-xl rounded-xl p-4 border border-amber-500/30 shadow-xl">
                  <div className="flex justify-between items-center mb-2">
                    <h3 className="text-lg font-bold text-white flex items-center gap-2">
                      <Star className="w-5 h-5 text-amber-400" fill="#fbbf24" />
                      My Gear Collection
                    </h3>
                    {isOwner && (
                      <button
                        onClick={() => setShowAddGearForm(true)}
                        className="px-3 py-1.5 bg-gradient-to-r from-amber-600 to-yellow-600 hover:from-amber-500 hover:to-yellow-500 text-white rounded-lg font-medium text-sm flex items-center gap-1.5 transition-all"
                        data-testid="add-gear-btn"
                      >
                        <Plus size={16} />
                        Add Gear
                      </button>
                    )}
                  </div>
                  
                  {/* Inline Stats */}
                  <div className="flex items-center gap-6">
                    <p className="text-gray-400 text-sm flex-1">
                      {isOwner 
                        ? "Showcase your gear and share ratings with the community."
                        : `${user?.name || 'This user'}&apos;s gear collection.`
                      }
                    </p>
                    <div className="flex items-center gap-4">
                      <div className="text-center">
                        <span className="text-lg font-bold text-amber-400">{userGear.length}</span>
                        <span className="text-xs text-gray-400 ml-1">Items</span>
                      </div>
                      <div className="text-center">
                        <span className="text-lg font-bold text-cyan-400">{userGear.filter(g => g.user_rating).length}</span>
                        <span className="text-xs text-gray-400 ml-1">Rated</span>
                      </div>
                      <div className="text-center">
                        <span className="text-lg font-bold text-green-400">{userGear.filter(g => g.user_review).length}</span>
                        <span className="text-xs text-gray-400 ml-1">Reviews</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Gear Grid */}
                {userGear.length === 0 ? (
                  <div className="bg-slate-800/50 backdrop-blur rounded-xl p-8 border border-amber-500/20 text-center">
                    <Star className="w-12 h-12 text-amber-400/50 mx-auto mb-4" />
                    <p className="text-gray-400 mb-4">
                      {isOwner 
                        ? "You haven&apos;t added any gear yet."
                        : "No gear in this collection yet."}
                    </p>
                    {isOwner && (
                      <button
                        onClick={() => setShowAddGearForm(true)}
                        className="px-6 py-3 bg-gradient-to-r from-amber-600 to-yellow-600 hover:from-amber-500 hover:to-yellow-500 text-white rounded-lg font-medium inline-flex items-center gap-2 transition-all"
                      >
                        <Plus size={18} />
                        Add Your First Gear
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                    {userGear.map((gear, index) => {
                      const hasSourceUrl = gear.source_url && gear.source_url.trim() !== '';
                      const hasUserReview = gear.user_review && gear.user_review.trim() !== '';
                      const CardWrapper = hasSourceUrl ? 'a' : 'div';
                      const cardProps = hasSourceUrl ? {
                        href: gear.source_url,
                        target: '_blank',
                        rel: 'noopener noreferrer'
                      } : {};
                      
                      return (
                        <CardWrapper
                          key={gear.gear_id || index}
                          {...cardProps}
                          className={`bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur rounded-lg border border-amber-500/20 overflow-hidden hover:border-amber-500/40 transition-all group relative ${hasSourceUrl ? 'cursor-pointer' : ''}`}
                          style={{ textDecoration: 'none', display: 'block' }}
                        >
                          {/* Delete Button - Only for owner */}
                          {isOwner && (
                            <button
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleDeleteGear(gear.gear_id, gear.gear_name);
                              }}
                              className="absolute top-2 left-2 z-20 w-5 h-5 bg-red-500/80 hover:bg-red-500 rounded-full flex items-center justify-center transition-all opacity-0 group-hover:opacity-100"
                              title="Remove from collection"
                            >
                              <X size={12} className="text-white" />
                            </button>
                          )}
                          
                          {/* Badge - Pro Review or BeatSeek Review */}
                          {(hasSourceUrl || hasUserReview) && (
                            <div className={`absolute top-2 right-2 z-10 px-2 py-0.5 ${hasSourceUrl ? 'bg-gradient-to-r from-amber-600 to-yellow-600' : 'bg-gradient-to-r from-cyan-600 to-blue-600'} text-white text-[10px] font-bold rounded-full shadow-lg flex items-center gap-1`}>
                              <Star size={10} fill="currentColor" />
                              {hasSourceUrl ? 'Pro Review' : 'BeatSeek'}
                            </div>
                          )}
                          
                          {/* Gear Image - slightly larger */}
                          {gear.image_url && (
                            <div className="h-32 relative overflow-hidden bg-slate-900">
                              <img
                                src={gear.image_url}
                                alt={gear.gear_name}
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                }}
                              />
                            </div>
                          )}
                          
                          {/* Gear Details */}
                          <div className="p-3">
                            <div className="flex items-start justify-between gap-2 mb-1.5">
                              <div className="min-w-0 flex-1">
                                <h4 className="font-semibold text-white text-sm group-hover:text-amber-300 transition-colors line-clamp-2 leading-tight">{gear.gear_name}</h4>
                                <p className="text-xs text-gray-400 truncate mt-0.5">{gear.gear_brand}</p>
                              </div>
                              <span className="px-1.5 py-0.5 bg-amber-500/20 text-amber-300 text-[10px] rounded capitalize flex-shrink-0">
                                {gear.gear_category || 'gear'}
                              </span>
                            </div>
                            
                            {/* Rating */}
                            {gear.user_rating && (
                              <div className="flex items-center gap-0.5 mb-1.5">
                                {[1, 2, 3, 4, 5].map((star) => (
                                  <Star
                                    key={star}
                                    size={14}
                                    fill={gear.user_rating >= star ? '#00ffff' : 'transparent'}
                                    stroke="#00ffff"
                                    strokeWidth={1.5}
                                  />
                                ))}
                                <span className="text-cyan-400 text-xs ml-1">{gear.user_rating}/5</span>
                              </div>
                            )}
                            
                            {/* Review - two lines max */}
                            {gear.user_review && (
                              <p className="text-xs text-gray-400 line-clamp-2 italic leading-relaxed">
                                &quot;{gear.user_review}&quot;
                              </p>
                            )}
                            
                            {/* Footer */}
                            <div className="flex items-center justify-between mt-2 pt-2 border-t border-amber-500/10">
                              <p className="text-[10px] text-gray-500">
                                {new Date(gear.added_at).toLocaleDateString()}
                              </p>
                              <div className="flex items-center gap-2">
                                {/* Discord Share Button */}
                                <button
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleShareGearToDiscord(gear);
                                  }}
                                  className="p-1 rounded hover:bg-indigo-500/20 transition-colors"
                                  title="Share to Discord"
                                >
                                  <svg width="14" height="14" viewBox="0 0 71 55" fill="#5865F2">
                                    <path d="M60.1045 4.8978C55.5792 2.8214 50.7265 1.2916 45.6527 0.41542C45.5603 0.39851 45.468 0.440769 45.4204 0.525289C44.7963 1.6353 44.105 3.0834 43.6209 4.2216C38.1637 3.4046 32.7345 3.4046 27.3892 4.2216C26.905 3.0581 26.1886 1.6353 25.5617 0.525289C25.5141 0.443589 25.4218 0.40133 25.3294 0.41542C20.2584 1.2888 15.4057 2.8186 10.8776 4.8978C10.8384 4.9147 10.8048 4.9429 10.7825 4.9795C1.57795 18.7309 -0.943561 32.1443 0.293408 45.3914C0.299005 45.4562 0.335386 45.5182 0.385761 45.5576C6.45866 50.0174 12.3413 52.7249 18.1147 54.5195C18.2071 54.5477 18.305 54.5139 18.3638 54.4378C19.7295 52.5728 20.9469 50.6063 21.9907 48.5383C22.0523 48.4172 21.9935 48.2735 21.8676 48.2256C19.9366 47.4931 18.0979 46.6 16.3292 45.5858C16.1893 45.5041 16.1781 45.304 16.3068 45.2082C16.679 44.9293 17.0513 44.6391 17.4067 44.3461C17.471 44.2926 17.5606 44.2813 17.6362 44.3151C29.2558 49.6202 41.8354 49.6202 53.3179 44.3151C53.3935 44.2785 53.4831 44.2898 53.5502 44.3433C53.9057 44.6363 54.2779 44.9293 54.6529 45.2082C54.7816 45.304 54.7732 45.5041 54.6333 45.5858C52.8646 46.6197 51.0259 47.4931 49.0921 48.2228C48.9662 48.2707 48.9102 48.4172 48.9718 48.5383C50.038 50.6034 51.2554 52.5699 52.5959 54.435C52.6519 54.5139 52.7526 54.5477 52.845 54.5195C58.6464 52.7249 64.529 50.0174 70.6019 45.5576C70.6551 45.5182 70.6887 45.459 70.6943 45.3942C72.1747 30.0791 68.2147 16.7757 60.1968 4.9823C60.1772 4.9429 60.1437 4.9147 60.1045 4.8978ZM23.7259 37.3253C20.2276 37.3253 17.3451 34.1136 17.3451 30.1693C17.3451 26.225 20.1717 23.0133 23.7259 23.0133C27.308 23.0133 30.1626 26.2532 30.1099 30.1693C30.1099 34.1136 27.2801 37.3253 23.7259 37.3253ZM47.3178 37.3253C43.8196 37.3253 40.9371 34.1136 40.9371 30.1693C40.9371 26.225 43.7637 23.0133 47.3178 23.0133C50.9 23.0133 53.7545 26.2532 53.7018 30.1693C53.7018 34.1136 50.9 37.3253 47.3178 37.3253Z"/>
                                  </svg>
                                </button>
                                {hasSourceUrl && (
                                  <span className="text-[10px] text-amber-400 font-medium flex items-center gap-0.5 group-hover:text-amber-300">
                                    Read More
                                    <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                    </svg>
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </CardWrapper>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Tab Content - Badges */}
        {activeTab === 'badges' && (
          <div className="space-y-6">
            {loadingBadges ? (
              <div className="bg-slate-800/50 backdrop-blur rounded-xl p-8 border border-red-500/20 text-center">
                <div className="text-white text-lg animate-pulse">Loading badges...</div>
              </div>
            ) : !badgesData ? (
              <div className="bg-slate-800/50 backdrop-blur rounded-xl p-8 border border-red-500/20 text-center">
                <p className="text-gray-400">Failed to load badges</p>
              </div>
            ) : (
              <>
                {/* Badge Summary */}
                <div className="bg-gradient-to-br from-slate-800/90 via-red-900/20 to-slate-800/90 backdrop-blur-xl rounded-2xl p-6 border border-red-500/30 shadow-xl">
                  <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                    <svg className="w-6 h-6 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                    </svg>
                    Achievement Summary
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    <div className="bg-slate-700/30 rounded-lg p-4 border border-red-500/20 text-center">
                      <div className="text-3xl font-bold text-red-400">{badgesData.summary.earned_badges}</div>
                      <div className="text-xs text-gray-400 mt-1">Earned</div>
                    </div>
                    <div className="bg-slate-700/30 rounded-lg p-4 border border-gray-500/20 text-center">
                      <div className="text-2xl font-bold text-gray-400">{badgesData.summary.common}</div>
                      <div className="text-xs text-gray-400 mt-1">Common</div>
                    </div>
                    <div className="bg-slate-700/30 rounded-lg p-4 border border-blue-500/20 text-center">
                      <div className="text-2xl font-bold text-blue-400">{badgesData.summary.rare}</div>
                      <div className="text-xs text-gray-400 mt-1">Rare</div>
                    </div>
                    <div className="bg-slate-700/30 rounded-lg p-4 border border-purple-500/20 text-center">
                      <div className="text-2xl font-bold text-purple-400">{badgesData.summary.epic}</div>
                      <div className="text-xs text-gray-400 mt-1">Epic</div>
                    </div>
                    <div className="bg-slate-700/30 rounded-lg p-4 border border-yellow-500/20 text-center">
                      <div className="text-2xl font-bold text-yellow-400">{badgesData.summary.legendary}</div>
                      <div className="text-xs text-gray-400 mt-1">Legendary</div>
                    </div>
                  </div>
                </div>

                {/* Music Achievements */}
                {badgesData.badges.filter(b => b.category === 'music').length > 0 && (
                  <div className="bg-gradient-to-br from-slate-800/90 via-cyan-900/20 to-slate-800/90 backdrop-blur-xl rounded-2xl p-6 border border-cyan-500/30 shadow-xl">
                    <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                      <svg className="w-6 h-6 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                      </svg>
                      Music Achievements
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {badgesData.badges.filter(b => b.category === 'music').map((badge) => (
                        <div
                          key={badge.id}
                          className={`bg-slate-700/30 rounded-lg p-4 border transition-all ${
                            badge.earned
                              ? 'border-cyan-500/30 hover:border-cyan-400/50'
                              : 'border-gray-500/20 opacity-60'
                          }`}
                        >
                          <div className="flex items-start gap-3">
                            <div className={`text-4xl ${badge.earned ? '' : 'grayscale opacity-50'}`}>
                              {badge.icon}
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <h4 className={`font-semibold ${badge.earned ? 'text-white' : 'text-gray-400'}`}>
                                  {badge.name}
                                </h4>
                                {badge.earned && (
                                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                                    badge.rarity === 'legendary' ? 'bg-yellow-500/20 text-yellow-300' :
                                    badge.rarity === 'epic' ? 'bg-purple-500/20 text-purple-300' :
                                    badge.rarity === 'rare' ? 'bg-blue-500/20 text-blue-300' :
                                    'bg-gray-500/20 text-gray-300'
                                  }`}>
                                    {badge.rarity}
                                  </span>
                                )}
                              </div>
                              <p className="text-xs text-gray-400 mb-2">{badge.description}</p>
                              
                              {badge.progress !== undefined && badge.required !== undefined && (
                                <div className="mt-2">
                                  <div className="flex justify-between text-xs text-gray-500 mb-1">
                                    <span>{badge.progress} / {badge.required}</span>
                                    <span>{Math.min(Math.round((badge.progress / badge.required) * 100), 100)}%</span>
                                  </div>
                                  <div className="w-full bg-slate-600 rounded-full h-2">
                                    <div
                                      className={`h-2 rounded-full transition-all ${
                                        badge.earned ? 'bg-cyan-500' : 'bg-gray-500'
                                      }`}
                                      style={{ width: `${Math.min((badge.progress / badge.required) * 100, 100)}%` }}
                                    />
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Social Achievements */}
                {badgesData.badges.filter(b => b.category === 'social').length > 0 && (
                  <div className="bg-gradient-to-br from-slate-800/90 via-purple-900/20 to-slate-800/90 backdrop-blur-xl rounded-2xl p-6 border border-purple-500/30 shadow-xl">
                    <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                      <svg className="w-6 h-6 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                      Social Achievements
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {badgesData.badges.filter(b => b.category === 'social').map((badge) => (
                        <div
                          key={badge.id}
                          className={`bg-slate-700/30 rounded-lg p-4 border transition-all ${
                            badge.earned
                              ? 'border-purple-500/30 hover:border-purple-400/50'
                              : 'border-gray-500/20 opacity-60'
                          }`}
                        >
                          <div className="flex items-start gap-3">
                            <div className={`text-4xl ${badge.earned ? '' : 'grayscale opacity-50'}`}>
                              {badge.icon}
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <h4 className={`font-semibold ${badge.earned ? 'text-white' : 'text-gray-400'}`}>
                                  {badge.name}
                                </h4>
                                {badge.earned && (
                                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                                    badge.rarity === 'legendary' ? 'bg-yellow-500/20 text-yellow-300' :
                                    badge.rarity === 'epic' ? 'bg-purple-500/20 text-purple-300' :
                                    badge.rarity === 'rare' ? 'bg-blue-500/20 text-blue-300' :
                                    'bg-gray-500/20 text-gray-300'
                                  }`}>
                                    {badge.rarity}
                                  </span>
                                )}
                              </div>
                              <p className="text-xs text-gray-400 mb-2">{badge.description}</p>
                              
                              {badge.progress !== undefined && badge.required !== undefined && (
                                <div className="mt-2">
                                  <div className="flex justify-between text-xs text-gray-500 mb-1">
                                    <span>{badge.progress} / {badge.required}</span>
                                    <span>{Math.min(Math.round((badge.progress / badge.required) * 100), 100)}%</span>
                                  </div>
                                  <div className="w-full bg-slate-600 rounded-full h-2">
                                    <div
                                      className={`h-2 rounded-full transition-all ${
                                        badge.earned ? 'bg-purple-500' : 'bg-gray-500'
                                      }`}
                                      style={{ width: `${Math.min((badge.progress / badge.required) * 100, 100)}%` }}
                                    />
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Special Badges */}
                {badgesData.badges.filter(b => b.category === 'special').length > 0 && (
                  <div className="bg-gradient-to-br from-slate-800/90 via-yellow-900/20 to-slate-800/90 backdrop-blur-xl rounded-2xl p-6 border border-yellow-500/30 shadow-xl">
                    <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                      <svg className="w-6 h-6 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                      </svg>
                      Special Badges
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {badgesData.badges.filter(b => b.category === 'special').map((badge) => (
                        <div
                          key={badge.id}
                          className="bg-slate-700/30 rounded-lg p-4 border border-yellow-500/30 hover:border-yellow-400/50 transition-all"
                        >
                          <div className="flex items-start gap-3">
                            <div className="text-4xl">
                              {badge.icon}
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <h4 className="font-semibold text-white">
                                  {badge.name}
                                </h4>
                                <span className={`text-xs px-2 py-0.5 rounded-full ${
                                  badge.rarity === 'legendary' ? 'bg-yellow-500/20 text-yellow-300' :
                                  badge.rarity === 'epic' ? 'bg-purple-500/20 text-purple-300' :
                                  badge.rarity === 'rare' ? 'bg-blue-500/20 text-blue-300' :
                                  'bg-gray-500/20 text-gray-300'
                                }`}>
                                  {badge.rarity}
                                </span>
                              </div>
                              <p className="text-xs text-gray-400">{badge.description}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Tab Content - Marketplace */}
        {activeTab === 'marketplace' && (
          <div className="space-y-6">
            {loadingMarketplace ? (
              <div className="bg-slate-800/50 backdrop-blur rounded-xl p-8 border border-cyan-500/20 text-center">
                <div className="text-white text-lg animate-pulse">Loading marketplace listings...</div>
              </div>
            ) : (
              <>
                {/* Search and Filter */}
                <div className="bg-gradient-to-br from-slate-800/90 via-purple-900/20 to-slate-800/90 backdrop-blur-xl rounded-2xl p-4 border border-purple-500/30 shadow-xl">
                  <div className="flex flex-col sm:flex-row gap-4">
                    <input
                      type="text"
                      placeholder="Search listings..."
                      value={marketplaceSearch}
                      onChange={(e) => setMarketplaceSearch(e.target.value)}
                      className="flex-1 px-4 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-cyan-500 transition-colors"
                    />
                    <select
                      value={marketplaceCategory}
                      onChange={(e) => setMarketplaceCategory(e.target.value)}
                      className="px-4 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-cyan-500 transition-colors cursor-pointer"
                    >
                      <option value="all">All Categories</option>
                      <option value="gear">Gear & Equipment</option>
                      <option value="merch">Merch & Promo</option>
                    </select>
                  </div>
                </div>

                {/* Gear Listings */}
                {(marketplaceCategory === 'all' || marketplaceCategory === 'gear') && marketplaceData.gear.filter(item => 
                  !marketplaceSearch || 
                  item.title.toLowerCase().includes(marketplaceSearch.toLowerCase()) ||
                  item.description.toLowerCase().includes(marketplaceSearch.toLowerCase())
                ).length > 0 && (
                  <div className="bg-gradient-to-br from-slate-800/90 via-cyan-900/20 to-slate-800/90 backdrop-blur-xl rounded-2xl p-6 border border-cyan-500/30 shadow-xl">
                    <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                      <svg className="w-6 h-6 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                      </svg>
                      Gear & Equipment ({marketplaceData.gear.filter(item => 
                        !marketplaceSearch || 
                        item.title.toLowerCase().includes(marketplaceSearch.toLowerCase()) ||
                        item.description.toLowerCase().includes(marketplaceSearch.toLowerCase())
                      ).length})
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {marketplaceData.gear
                        .filter(item => 
                          !marketplaceSearch || 
                          item.title.toLowerCase().includes(marketplaceSearch.toLowerCase()) ||
                          item.description.toLowerCase().includes(marketplaceSearch.toLowerCase())
                        )
                        .map((item) => (
                          <div
                            key={item.id}
                            className="bg-slate-700/30 rounded-lg p-4 border border-cyan-500/20 hover:border-cyan-400/50 transition-all"
                          >
                            <div className="flex justify-between items-start mb-2">
                              <h4 className="font-semibold text-white text-lg">{item.title}</h4>
                              <span className="text-cyan-400 font-bold text-xl">${item.price}</span>
                            </div>
                            <p className="text-gray-300 text-sm mb-3 line-clamp-2">{item.description}</p>
                            <div className="flex flex-wrap gap-2 text-xs">
                              <span className="bg-slate-600/50 px-2 py-1 rounded text-gray-300">
                                {item.condition}
                              </span>
                              <span className="bg-slate-600/50 px-2 py-1 rounded text-gray-300">
                                {item.category}
                              </span>
                              <span className="bg-slate-600/50 px-2 py-1 rounded text-gray-300">
                                📍 {item.location}
                              </span>
                              {item.shipping_available && (
                                <span className="bg-cyan-600/30 px-2 py-1 rounded text-cyan-300">
                                  📦 Ships
                                </span>
                              )}
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                )}

                {/* Merch Listings */}
                {(marketplaceCategory === 'all' || marketplaceCategory === 'merch') && marketplaceData.merch.filter(item => 
                  !marketplaceSearch || 
                  item.product_name.toLowerCase().includes(marketplaceSearch.toLowerCase()) ||
                  item.description.toLowerCase().includes(marketplaceSearch.toLowerCase())
                ).length > 0 && (
                  <div className="bg-gradient-to-br from-slate-800/90 via-purple-900/20 to-slate-800/90 backdrop-blur-xl rounded-2xl p-6 border border-purple-500/30 shadow-xl">
                    <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                      <svg className="w-6 h-6 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                      </svg>
                      Merch & Promo ({marketplaceData.merch.filter(item => 
                        !marketplaceSearch || 
                        item.product_name.toLowerCase().includes(marketplaceSearch.toLowerCase()) ||
                        item.description.toLowerCase().includes(marketplaceSearch.toLowerCase())
                      ).length})
                    </h3>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                      {marketplaceData.merch
                        .filter(item => 
                          !marketplaceSearch || 
                          item.product_name.toLowerCase().includes(marketplaceSearch.toLowerCase()) ||
                          item.description.toLowerCase().includes(marketplaceSearch.toLowerCase())
                        )
                        .map((item) => (
                          <div
                            key={item.id}
                            className="bg-slate-700/30 rounded-lg overflow-hidden border border-purple-500/20 hover:border-purple-400/50 transition-all group"
                          >
                            {item.image_url && (
                              <div className="aspect-square bg-slate-800 overflow-hidden">
                                <img 
                                  src={item.image_url} 
                                  alt={item.product_name}
                                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                />
                              </div>
                            )}
                            <div className="p-3">
                              <h4 className="font-semibold text-white text-sm mb-1 truncate">{item.product_name}</h4>
                              <p className="text-purple-400 font-bold text-lg">${item.price}</p>
                              <span className="text-xs text-gray-400">{item.product_type}</span>
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                )}

                {/* Empty State */}
                {marketplaceData.gear.length === 0 && marketplaceData.merch.length === 0 && (
                  <div className="bg-slate-800/50 backdrop-blur rounded-xl p-12 border border-gray-500/20 text-center">
                    <svg className="w-16 h-16 text-gray-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                    </svg>
                    <h3 className="text-xl font-semibold text-gray-300 mb-2">No Listings Yet</h3>
                    <p className="text-gray-400">This user hasn&apos;t posted any marketplace listings.</p>
                  </div>
                )}

                {/* No Results State */}
                {marketplaceSearch && 
                 marketplaceData.gear.filter(item => 
                   item.title.toLowerCase().includes(marketplaceSearch.toLowerCase()) ||
                   item.description.toLowerCase().includes(marketplaceSearch.toLowerCase())
                 ).length === 0 && 
                 marketplaceData.merch.filter(item => 
                   item.product_name.toLowerCase().includes(marketplaceSearch.toLowerCase()) ||
                   item.description.toLowerCase().includes(marketplaceSearch.toLowerCase())
                 ).length === 0 && 
                 (marketplaceData.gear.length > 0 || marketplaceData.merch.length > 0) && (
                  <div className="bg-slate-800/50 backdrop-blur rounded-xl p-8 border border-gray-500/20 text-center">
                    <p className="text-gray-400">No listings match &quot;{marketplaceSearch}&quot;</p>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Promos Tab */}
        {activeTab === 'promos' && (
          <div className="space-y-6">
            {loadingPromos ? (
              <div className="bg-slate-800/50 backdrop-blur rounded-xl p-8 border border-pink-500/20 text-center">
                <div className="text-white text-lg animate-pulse">Loading promos...</div>
              </div>
            ) : (
              <>
                {promosData.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {promosData.map((promo) => (
                      <div
                        key={promo.id}
                        className="bg-gradient-to-br from-slate-800/90 via-pink-900/20 to-slate-800/90 backdrop-blur-xl rounded-2xl p-6 border border-pink-500/30 shadow-xl hover:border-pink-400/50 transition-all"
                      >
                        {/* Promo Header */}
                        <div className="flex justify-between items-start mb-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <span className="px-3 py-1 bg-pink-500/20 border border-pink-500/40 rounded-full text-pink-300 text-xs font-semibold uppercase">
                                {promo.category === 'mix_track' ? '🎵 Mix/Track' : promo.category === 'event' ? '🎉 Event' : '📢 General'}
                              </span>
                              {promo.is_boosted && (
                                <span className="px-2 py-1 bg-yellow-500/20 border border-yellow-500/40 rounded-full text-yellow-300 text-xs font-semibold">
                                  ⭐ Boosted
                                </span>
                              )}
                            </div>
                            <h3 className="text-xl font-bold text-white mb-2">{promo.title}</h3>
                            <p className="text-gray-300 text-sm line-clamp-3">{promo.description}</p>
                          </div>
                        </div>

                        {/* Genre Tags */}
                        {promo.genre_tags && promo.genre_tags.length > 0 && (
                          <div className="flex flex-wrap gap-2 mb-4">
                            {promo.genre_tags.map((tag, idx) => (
                              <span key={idx} className="px-2 py-1 bg-slate-700/50 rounded text-gray-300 text-xs">
                                #{tag}
                              </span>
                            ))}
                          </div>
                        )}

                        {/* Engagement Stats */}
                        <div className="flex items-center gap-6 mb-4 text-sm text-gray-400">
                          <span className="flex items-center gap-1">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                            {promo.views_count || 0} views
                          </span>
                          <span className="flex items-center gap-1">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                            </svg>
                            {promo.likes_count || 0} likes
                          </span>
                          <span className="flex items-center gap-1">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                            </svg>
                            {promo.comments_count || 0} comments
                          </span>
                        </div>

                        {/* View/Navigate Links (for all users) */}
                        <div className="flex items-center gap-4 mb-4 text-sm">
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              console.log('🔴 View Promo clicked! promoId:', promo.id);
                              // Navigate to home page with state to open promo
                              navigate('/', { 
                                state: { 
                                  openPromoSpot: true, 
                                  promoId: promo.id 
                                } 
                              });
                            }}
                            className="text-pink-400 hover:text-pink-300 transition-colors underline cursor-pointer bg-transparent border-none"
                          >
                            View Promo
                          </button>
                          {promo.linked_event_id && (
                            <>
                              <span className="text-gray-600">•</span>
                              <button
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  console.log('🔴 View on Calendar clicked! eventId:', promo.linked_event_id);
                                  // Navigate to home page with state to open event calendar
                                  navigate('/', { 
                                    state: { 
                                      openEventCalendar: true, 
                                      eventId: promo.linked_event_id 
                                    } 
                                  });
                                }}
                                className="text-cyan-400 hover:text-cyan-300 transition-colors underline cursor-pointer bg-transparent border-none"
                              >
                                View on Calendar
                              </button>
                            </>
                          )}
                        </div>

                        {/* Action Buttons (only for owner) */}
                        {isOwner && (
                          <div className="flex gap-2 pt-3 border-t border-pink-500/20">
                            <button
                              onClick={() => handleEditPromo(promo)}
                              className="flex-1 bg-pink-500/20 hover:bg-pink-500/30 border border-pink-500/40 text-pink-300 px-3 py-1.5 rounded-lg transition-all text-sm font-medium flex items-center justify-center gap-1.5"
                            >
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                              Edit
                            </button>
                            <button
                              onClick={() => handleDeletePromo(promo.id)}
                              className="flex-1 bg-red-500/20 hover:bg-red-500/30 border border-red-500/40 text-red-300 px-3 py-1.5 rounded-lg transition-all text-sm font-medium flex items-center justify-center gap-1.5"
                            >
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                              Delete
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="bg-slate-800/50 backdrop-blur rounded-xl p-12 border border-gray-500/20 text-center">
                    <div className="text-6xl mb-4">📢</div>
                    <h3 className="text-xl font-semibold text-gray-300 mb-2">No Promos Yet</h3>
                    <p className="text-gray-400">
                      {isOwner ? "You haven't created any promos yet." : "This user hasn't created any promos yet."}
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        </div> {/* End of Tab Content Container */}
      </div>

      {/* View All Modal */}
      {viewAllModal.isOpen && (
        <div 
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={closeViewAllModal}
        >
          <div 
            className="bg-gradient-to-br from-slate-800 via-slate-900 to-slate-800 rounded-2xl border border-cyan-500/30 shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-cyan-500/20">
              <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                {viewAllModal.section === 'Friends' && <Users size={28} className="text-green-400" />}
                {viewAllModal.section === 'Followers' && (
                  <svg className="w-7 h-7 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                )}
                {viewAllModal.section === 'Following' && (
                  <svg className="w-7 h-7 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                )}
                All {viewAllModal.section}
                <span className="text-sm text-gray-400 font-normal ml-2">({viewAllModal.users.length})</span>
              </h2>
              <button
                onClick={closeViewAllModal}
                className="text-gray-400 hover:text-white transition-colors p-2 hover:bg-slate-700 rounded-lg"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Content - Scrollable Grid */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-100px)]">
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                {viewAllModal.users.map((user) => {
                  const sectionColor = 
                    viewAllModal.section === 'Friends' ? 'green' :
                    viewAllModal.section === 'Followers' ? 'blue' : 'purple';
                  
                  return (
                    <div
                      key={user.id}
                      className={`bg-slate-700/30 rounded-lg p-3 border border-${sectionColor}-500/20 hover:border-${sectionColor}-400/40 transition-all group relative`}
                    >
                      {/* Avatar with Recently Active Indicator */}
                      <div
                        className="relative mb-2 cursor-pointer"
                        onClick={() => {
                          closeViewAllModal();
                          navigate(`/profile/${user.id}`);
                        }}
                      >
                        <div className={`w-16 h-16 mx-auto rounded-full bg-gradient-to-br from-${sectionColor}-600 to-${sectionColor === 'green' ? 'emerald' : sectionColor === 'blue' ? 'cyan' : 'pink'}-600 flex items-center justify-center text-white font-bold text-xl overflow-hidden border-2 border-${sectionColor}-500/50 group-hover:border-${sectionColor}-400 transition-all`}>
                          {user.profile_picture ? (
                            <img src={`${API}${user.profile_picture.replace('/uploads/', '/api/uploads/')}`} alt={user.name} className="w-full h-full object-cover" />
                          ) : (
                            user.name?.charAt(0).toUpperCase() || '?'
                          )}
                        </div>
                        {isRecentlyActive(user.last_seen) && (
                          <div className="absolute bottom-0 right-1/4 w-4 h-4 bg-green-500 border-2 border-slate-800 rounded-full"></div>
                        )}
                      </div>
                      
                      {/* Name */}
                      <p
                        className={`text-sm text-white font-medium text-center truncate cursor-pointer group-hover:text-${sectionColor}-300 transition-colors`}
                        onClick={() => {
                          closeViewAllModal();
                          navigate(`/profile/${user.id}`);
                        }}
                      >
                        {user.name}
                      </p>
                      
                      {/* Last Seen */}
                      <p className="text-xs text-gray-500 text-center mt-1">
                        {formatLastSeen(user.last_seen)}
                      </p>
                      
                      {/* Action Button */}
                      {currentUser && (currentUser.user_id !== user.id && currentUser.id !== user.id) && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (user.viewer_is_following) {
                              handleUnfollowUser(user.id);
                            } else {
                              handleFollowUser(user.id);
                            }
                          }}
                          className={`mt-2 w-full px-2 py-1 rounded text-xs font-medium transition-all ${
                            user.viewer_is_following
                              ? 'bg-slate-600 hover:bg-slate-500 text-white'
                              : `bg-gradient-to-r from-${sectionColor}-600 to-${sectionColor === 'green' ? 'emerald' : sectionColor === 'blue' ? 'cyan' : 'pink'}-600 hover:from-${sectionColor}-500 hover:to-${sectionColor === 'green' ? 'emerald' : sectionColor === 'blue' ? 'cyan' : 'pink'}-500 text-white`
                          }`}
                        >
                          {user.viewer_is_following ? 'Unfollow' : 'Follow'}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Video Upload Modal */}
      <VideoUploadModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        onUploadSuccess={handleVideoUploadSuccess}
        currentUser={currentUser}
      />

      {/* Video Edit Modal */}
      <VideoEditModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setVideoToEdit(null);
        }}
        video={videoToEdit}
        onSaveSuccess={handleVideoEditSuccess}
      />

      {/* Edit Profile Modal */}
      {/* Edit Promo Modal */}
      {showEditPromoModal && promoToEdit && (
        <div 
          className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => {
            setShowEditPromoModal(false);
            setPromoToEdit(null);
          }}
        >
          <div 
            className="bg-gradient-to-br from-slate-800 via-slate-900 to-slate-800 rounded-2xl border border-pink-500/30 shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-pink-500/20 sticky top-0 bg-slate-800/95 backdrop-blur z-10">
              <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                <svg className="w-7 h-7 text-pink-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                Edit Promo
              </h2>
              <button
                onClick={() => {
                  setShowEditPromoModal(false);
                  setPromoToEdit(null);
                }}
                className="text-gray-400 hover:text-white transition-colors p-2 hover:bg-slate-700 rounded-lg"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6">
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  const formData = new FormData(e.target);
                  const updatedData = {
                    title: formData.get('title'),
                    description: formData.get('description'),
                    genre_tags: formData.get('genre_tags')?.split(',').map(t => t.trim()).filter(Boolean) || []
                  };
                  handleUpdatePromo(promoToEdit.id, updatedData);
                }}
                className="space-y-4"
              >
                {/* Title */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Title *
                  </label>
                  <input
                    type="text"
                    name="title"
                    defaultValue={promoToEdit.title}
                    required
                    className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-pink-500 transition-colors"
                    placeholder="Enter promo title..."
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Description *
                  </label>
                  <textarea
                    name="description"
                    defaultValue={promoToEdit.description}
                    required
                    rows="4"
                    className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-pink-500 transition-colors resize-none"
                    placeholder="Tell people about this promo..."
                  />
                </div>

                {/* Genre Tags */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Genre Tags (comma-separated)
                  </label>
                  <input
                    type="text"
                    name="genre_tags"
                    defaultValue={promoToEdit.genre_tags?.join(', ') || ''}
                    className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-pink-500 transition-colors"
                    placeholder="e.g., Techno, House, Deep House"
                  />
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowEditPromoModal(false);
                      setPromoToEdit(null);
                    }}
                    className="flex-1 bg-slate-700 hover:bg-slate-600 text-white px-6 py-3 rounded-lg transition-all font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 bg-gradient-to-r from-pink-600 to-red-600 hover:from-pink-500 hover:to-red-500 text-white px-6 py-3 rounded-lg transition-all font-medium"
                  >
                    Save Changes
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Favorites Modal */}
      {showFavoritesModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-900 rounded-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden border border-purple-500/30">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b border-slate-700">
              <div className="flex items-center gap-3">
                <Heart className="w-6 h-6 text-purple-400" fill="#a855f7" />
                <h2 className="text-xl font-bold text-white">My Favorites</h2>
              </div>
              <button
                onClick={() => setShowFavoritesModal(false)}
                className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>

            {/* Tabs */}
            <div className="flex border-b border-slate-700">
              <button
                onClick={() => setFavoritesActiveTab('music')}
                className={`flex-1 py-3 px-4 text-sm font-medium transition-colors ${
                  favoritesActiveTab === 'music' 
                    ? 'text-cyan-400 border-b-2 border-cyan-400 bg-cyan-500/10' 
                    : 'text-gray-400 hover:text-gray-200'
                }`}
              >
                🎵 Music ({favoriteTracks.length})
              </button>
              <button
                onClick={() => setFavoritesActiveTab('venues')}
                className={`flex-1 py-3 px-4 text-sm font-medium transition-colors ${
                  favoritesActiveTab === 'venues' 
                    ? 'text-red-400 border-b-2 border-red-400 bg-red-500/10' 
                    : 'text-gray-400 hover:text-gray-200'
                }`}
              >
                🏢 Venues ({savedVenues.length})
              </button>
            </div>

            {/* Content */}
            <div className="p-4 overflow-y-auto max-h-[calc(80vh-140px)]">
              {loadingFavorites ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-400"></div>
                </div>
              ) : favoritesActiveTab === 'music' ? (
                /* Music Favorites */
                favoriteTracks.length === 0 ? (
                  <div className="text-center py-8 text-gray-400">
                    <div className="text-4xl mb-2">🎵</div>
                    <p>No favorite tracks yet</p>
                    <p className="text-sm">Click the heart on tracks to save them here</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {favoriteTracks.map((track) => (
                      <div key={track.id} className="bg-slate-800/50 rounded-lg p-3 flex items-center gap-3 group">
                        <div className="w-12 h-12 bg-slate-700 rounded-lg flex items-center justify-center overflow-hidden flex-shrink-0">
                          {track.album_art ? (
                            <img src={track.album_art} alt={track.track_name} className="w-full h-full object-cover" />
                          ) : (
                            <span className="text-2xl">🎵</span>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-white font-medium truncate">{track.track_name}</div>
                          <div className="text-gray-400 text-sm truncate">{track.artist}</div>
                        </div>
                        <button
                          onClick={() => removeFavoriteTrack(track.id)}
                          className="p-2 text-gray-400 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-all"
                          title="Remove from favorites"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )
              ) : (
                /* Venues Favorites */
                savedVenues.length === 0 ? (
                  <div className="text-center py-8 text-gray-400">
                    <div className="text-4xl mb-2">🏢</div>
                    <p>No saved venues yet</p>
                    <p className="text-sm">Click the heart on venues in the Directory to save them here</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {savedVenues.map((venue) => (
                      <div key={venue.id} className="bg-slate-800/50 rounded-lg overflow-hidden group relative">
                        {venue.photo_url && (
                          <div className="h-24 overflow-hidden">
                            <img src={venue.photo_url} alt={venue.name} className="w-full h-full object-cover" />
                          </div>
                        )}
                        <div className="p-3">
                          <div className="text-white font-medium truncate">{venue.name}</div>
                          <div className="text-gray-400 text-xs truncate">{venue.address}</div>
                          <div className="flex gap-2 mt-2">
                            {venue.google_maps_url && (
                              <a 
                                href={venue.google_maps_url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="flex-1 text-center py-1.5 text-xs bg-cyan-500/20 text-cyan-400 rounded hover:bg-cyan-500/30 transition-colors"
                              >
                                Maps
                              </a>
                            )}
                            {venue.website && (
                              <a 
                                href={venue.website} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="flex-1 text-center py-1.5 text-xs bg-slate-700 text-white rounded hover:bg-slate-600 transition-colors"
                              >
                                Site
                              </a>
                            )}
                          </div>
                        </div>
                        <button
                          onClick={() => removeSavedVenue(venue.google_place_id)}
                          className="absolute top-2 right-2 p-1.5 bg-black/50 rounded-full text-gray-300 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-all"
                          title="Remove from favorites"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )
              )}
            </div>
          </div>
        </div>
      )}

      <EditProfileModal
        isOpen={showEditProfileModal}
        onClose={() => setShowEditProfileModal(false)}
        user={user}
        onUpdate={async (updatedData) => {
          // Refresh profile data after save
          setShowEditProfileModal(false);
          // Trigger a re-fetch of profile data
          window.location.reload();
        }}
      />

      {/* Local Whisper Modal — opens when user taps the Whisper button in the hero header */}
      {showLocalWhisper && profileData && profileData.user && (() => {
        const API = process.env.REACT_APP_BACKEND_URL || '';
        const rawAvatar = profileData.user.profile_picture;
        // Prefix relative avatar paths with backend URL so the image actually loads on prod
        const resolvedAvatar = rawAvatar
          ? (rawAvatar.startsWith('http') || rawAvatar.startsWith('data:') ? rawAvatar : `${API}${rawAvatar}`)
          : null;
        return (
          <WhisperModal
            isOpen={showLocalWhisper}
            onClose={() => setShowLocalWhisper(false)}
            otherUser={{
              user_id: profileData.user.id,
              id: profileData.user.id,
              user_name: profileData.user.name,
              name: profileData.user.name,
              user_avatar: resolvedAvatar,
              avatar: resolvedAvatar,
              online: false,
            }}
            currentUser={currentUser}
            globalEmotes={globalEmotes}
            customEmotes={customEmotes}
          />
        );
      })()}
      
      {/* Mobile: Back to search results pill */}
      {typeof window !== 'undefined' && window.innerWidth <= 768 && sessionStorage.getItem('beatseek_search_state') && (
        <button
          onClick={() => navigate('/?tab=social&restoreSearch=1')}
          data-testid="back-to-results-pill"
          style={{
            position: 'fixed',
            bottom: '86px',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 9500,
            padding: '10px 18px',
            borderRadius: '999px',
            background: 'linear-gradient(135deg, rgba(0, 212, 255, 0.95), rgba(0, 153, 204, 0.95))',
            border: 'none',
            color: '#001018',
            fontWeight: 700,
            fontSize: '0.85rem',
            boxShadow: '0 4px 20px rgba(0, 212, 255, 0.4)',
            cursor: 'pointer',
            whiteSpace: 'nowrap'
          }}
        >
          ← Back to results
        </button>
      )}

      {/* Mutual Friends Modal */}
      {profileData && profileData.user && (
        <MutualFriendsModal
          isOpen={showMutualModal}
          onClose={() => setShowMutualModal(false)}
          targetUserId={userId}
          targetUserName={profileData.user.name}
          viewerId={currentUser?.user_id || currentUser?.id}
        />
      )}
      
      {/* Add Gear Modal */}
      {showAddGearForm && (
        <AddGearReviewForm
          onClose={() => setShowAddGearForm(false)}
          onSuccess={() => {
            setShowAddGearForm(false);
            // Refresh gear data
            const fetchGear = async () => {
              try {
                const API = process.env.REACT_APP_BACKEND_URL || '';
                const response = await fetch(`${API}/api/gear/user/${userId}/public`);
                if (response.ok) {
                  const data = await response.json();
                  setUserGear(data.gear || []);
                }
              } catch (error) {
                console.error('Error refreshing gear:', error);
              }
            };
            fetchGear();
          }}
        />
      )}
      
      {/* Discord Share Modal */}
      {showDiscordShareModal && discordShareData && (
        <DiscordShareModal
          isOpen={showDiscordShareModal}
          onClose={() => {
            setShowDiscordShareModal(false);
            setDiscordShareData(null);
          }}
          shareType="gear"
          trackData={discordShareData}
        />
      )}
    </div>
  );
};

export default PublicProfilePage;
